import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { salesAPI, inventoryAPI, itemsAPI, settingsAPI, clientsAPI, cashAPI, getErrorMessage, catalogsAPI, printAPI, vatAPI, authAPI } from '@/lib/api';
import { TABLE_DENSITY } from '@/lib/tableDensity';
import { useGlobalFilters } from '@/hooks/useGlobalFilters';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, ShoppingCart, Trash2, Search, Smartphone, Package, Check, ChevronsUpDown, AlertCircle, Eye, Edit, History, XCircle, Clock, User, CheckCircle, DollarSign, Percent, CreditCard, Banknote, Building, Wallet, Smartphone as MobileIcon, MapPin, Store, Barcode, Printer, FileText, Receipt } from 'lucide-react';
import { formatCurrency, formatDateTime, formatDisplayCode, formatDisplayOnlyCode, cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import ExportButton from '@/components/ExportButton';
import { useBarcodeScanner, ScannerIndicator } from '@/components/BarcodeScanner';
import SalePrintDialog from '@/components/SalePrintDialog';
import MissingMasterDataDialog from '@/components/MissingMasterDataDialog';
import FiscalSaleDialog from '@/components/FiscalSaleDialog';
import { UniversalCrudActions } from '@/components/UniversalCrudActions';
import { ModulePageHero } from '@/components/common/ModulePageHero';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { ModuleStateBoundary } from '@/components/common/ModuleStateBoundary';
import { ResizableTableHandle } from '@/components/common/ResizableTableHandle';
import { DEFAULT_TABLE_PAGE_SIZE, paginateItems, TablePaginationBar } from '@/components/common/TablePaginationBar';
import { SalesFiltersDeck } from '@/components/sales/SalesFiltersDeck';
import SalesCashIntegrationPanel from '@/components/sales/SalesCashIntegrationPanel';
import { safeRequest } from '@/lib/safeRequest';
import { useFetchTelemetry } from '@/hooks/useFetchTelemetry';
import { useResizableTableColumns } from '@/hooks/useResizableTableColumns';
import { useLanguage } from '@/i18n';
import { applyCleanOrLegacyParam, buildHandleMaps, resolveHandleOrLegacyId } from '@/lib/queryHandles';
import { buildMaputoRangeBoundary, buildTranslatedPeriodOptions, formatSelectedDateRangeLabel, normalizeDateFilterValue } from '@/lib/periodFilters';

const ACCOUNT_TYPE_ICONS = {
  cash: Banknote,
  pos: CreditCard,
  bank_transfer: Building,
  mobile_money: MobileIcon
};

const statusColors = {
  draft: 'bg-slate-100 text-slate-800',
  confirmed: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
  completed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-800'
};

const statusLabels = {
  draft: 'Rascunho',
  confirmed: 'Confirmada',
  paid: 'Paga',
  completed: 'Concluída',
  cancelled: 'Cancelada'
};

// Session storage key for last used location (menos persistente no browser)
const LAST_LOCATION_KEY = 'cg_business_last_sale_location';
const readLastLocationId = () => window.sessionStorage.getItem(LAST_LOCATION_KEY);
const writeLastLocationId = (locationId) => {
  if (!locationId) return;
  window.sessionStorage.setItem(LAST_LOCATION_KEY, locationId);
};
const DEFAULT_CASH_PAYMENT_LABEL = 'Numerário';
const INVENTORY_TABLE_HEAD_CLASS = TABLE_DENSITY.head;
const INVENTORY_TABLE_CELL_CLASS = TABLE_DENSITY.cell;
const INVENTORY_TABLE_ROW_CLASS = TABLE_DENSITY.row;
const SALES_SELECTOR_COLUMN_STYLE = { width: 34, minWidth: 34, maxWidth: 34 };
const SALES_TABLE_COLUMN_WIDTHS = {
  selector: 34,
  sale: 132,
  operation: 190,
  sku: 138,
  quantity: 72,
  total: 108,
  status: 98,
  actions: 118,
};
const CANCELLED_SALES_TABLE_COLUMN_WIDTHS = {
  selector: 52,
  document: 170,
  datetime: 164,
  customer: 180,
  cancelled_by: 156,
  amount: 118,
  reason: 240,
  stock: 118,
  actions: 130,
};

// Status options for filter
const SALES_STATUS_OPTIONS = ['draft', 'confirmed', 'paid', 'completed'];

export default function SalesPage() {
  const navigate = useNavigate();
  const { t, tPeriod, language } = useLanguage();
  const locale = language === 'en' ? 'en-GB' : 'pt-PT';
  // URL Search Params for filter persistence
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsString = searchParams.toString();
  const requestedLocationHandle = searchParams.get('location');
  const requestedSalesFilterLocationHandle = searchParams.get('sales_location');
  const requestedLegacyLocationId = searchParams.get('location_id');
  const requestedLegacySalesLocationId = searchParams.get('sales_location_id');
  
  // Global filters
  const { filters, setFilters } = useGlobalFilters({
    period: searchParams.get('period') || 'all',
    location_id: searchParams.get('sales_location_id') || null,
    employee_id: searchParams.get('employee_id') || null,
    status: searchParams.get('status') || null,
    payment_account_id: searchParams.get('payment_account_id') || null,
    custom_start: normalizeDateFilterValue(searchParams.get('custom_start')) || null,
    custom_end: normalizeDateFilterValue(searchParams.get('custom_end')) || null,
  });
  
  const [sales, setSales] = useState([]);
  const [cancelledSales, setCancelledSales] = useState([]);
  const [selectedSaleIds, setSelectedSaleIds] = useState([]);
  const [products, setProducts] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [itemSelectDialogOpen, setItemSelectDialogOpen] = useState(false);
  const [availableItems, setAvailableItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const { user, hasRole } = useAuth();
  const { partialIssues, captureFetchTelemetry, resetFetchTelemetry } = useFetchTelemetry();
  
  const isAdmin = hasRole(['admin']);
  const canEdit = hasRole(['admin', 'store_manager', 'supervisor']);
  const canManageSaleEdits = hasRole(['admin', 'store_manager']);
  const canManageCashSession = hasRole(['admin', 'store_manager', 'accountant', 'cashier']);

  const urlTab = searchParams.get('tab');
  const [activeSalesTab, setActiveSalesTab] = useState(
    urlTab === 'cash' ? 'cash' : (urlTab === 'cancelled' ? 'cancelled' : 'sales')
  );
  const [activeCashSession, setActiveCashSession] = useState(null);
  const [loadingCashSession, setLoadingCashSession] = useState(false);
  const [purgeCancelledDialog, setPurgeCancelledDialog] = useState({ open: false, sale: null });
  const [adminActionLoading, setAdminActionLoading] = useState(false);
  const skipNextSalesReloadRef = useRef(false);
  const skipSalesFilterHydrationRef = useRef(false);
  
  // Location State (P1.1)
  const [selectedLocation, setSelectedLocation] = useState(null);
  const locationHandleMaps = useMemo(() => buildHandleMaps(locations, (location) => location.name), [locations]);
  const { getColumnStyle: getSalesColumnStyle, startColumnResize: startSalesColumnResize } = useResizableTableColumns({
    storageKey: 'cg-sales-list-columns',
    defaultWidths: SALES_TABLE_COLUMN_WIDTHS,
    minWidth: 72,
  });
  const { getColumnStyle: getCancelledSalesColumnStyle, startColumnResize: startCancelledSalesColumnResize } = useResizableTableColumns({
    storageKey: 'cg-cancelled-sales-list-columns',
    defaultWidths: CANCELLED_SALES_TABLE_COLUMN_WIDTHS,
    minWidth: 84,
  });
  const periodOptions = useMemo(() => buildTranslatedPeriodOptions(tPeriod), [tPeriod]);
  const selectedPeriodLabel = useMemo(() => {
    if (filters.period === 'custom') {
      const customLabel = formatSelectedDateRangeLabel(filters.custom_start, filters.custom_end, locale);
      if (customLabel) return customLabel;
    }
    return periodOptions.find((option) => option.value === (filters.period || 'all'))?.label || t('dashboard.selectedPeriod');
  }, [filters.custom_end, filters.custom_start, filters.period, locale, periodOptions, t]);
  
  // POS State
  const [cart, setCart] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [defaultClient, setDefaultClient] = useState(null);
  const [clientSearchOpen, setClientSearchOpen] = useState(false);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [clientCreateOpen, setClientCreateOpen] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  const [clientForm, setClientForm] = useState({
    name: '',
    phone: '',
    email: '',
    nuit: '',
    address: '',
    city: '',
    contact_person: '',
    client_type: 'individual'
  });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [payments, setPayments] = useState([]);
  const [saleDiscountValue, setSaleDiscountValue] = useState(0);
  const [saleDiscountPercent, setSaleDiscountPercent] = useState(0);
  const [saleNotes, setSaleNotes] = useState('');
  const [createAsDraft, setCreateAsDraft] = useState(false);
  const [saleRegisteredAt, setSaleRegisteredAt] = useState(''); // Custom date/time
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productAvailabilityLoading, setProductAvailabilityLoading] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [selectedItemForSale, setSelectedItemForSale] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Margin override state (P0-POS-01)
  const [marginOverrideOpen, setMarginOverrideOpen] = useState(false);
  const [marginWarning, setMarginWarning] = useState(null);
  const [overridePin, setOverridePin] = useState('');
  const [overrideAuthorizers, setOverrideAuthorizers] = useState([]);
  const [overrideAuthorizerId, setOverrideAuthorizerId] = useState('');
  const [loadingOverrideAuthorizers, setLoadingOverrideAuthorizers] = useState(false);
  const [pendingSalePayload, setPendingSalePayload] = useState(null);
  const [overrideProcessing, setOverrideProcessing] = useState(false);
  const profitNegative = marginWarning?.profit_negative || (marginWarning?.warnings || []).some(w => (w.profit_value ?? 0) < 0);
  const requiresPin = Boolean((marginWarning?.warnings || []).length);
  const selectedOverrideAuthorizer = useMemo(
    () => overrideAuthorizers.find((item) => item.id === overrideAuthorizerId) || null,
    [overrideAuthorizerId, overrideAuthorizers],
  );
  
  // VAT/IVA State - P0 OBRIGATÓRIO
  const [saleVatRegime, setSaleVatRegime] = useState('inclusive'); // inclusive, exclusive, exempt
  const [vatConfig, setVatConfig] = useState({ default_rate: 16.0, default_regime: 'inclusive' });
  
  // Payment entry state
  const [selectedPaymentAccount, setSelectedPaymentAccount] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [sessionEmployeeId, setSessionEmployeeId] = useState(null);

  const normalizeLabel = useCallback((value) => {
    if (!value) return '';
    return String(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/^\./, '')
      .trim()
      .toLowerCase();
  }, []);

  const getDefaultCashAccount = useCallback((accountsList = []) => {
    if (!accountsList?.length) return null;

    const target = normalizeLabel(DEFAULT_CASH_PAYMENT_LABEL);
    const byExactMethod = accountsList.find(acc => normalizeLabel(acc.payment_method_name) === target);
    if (byExactMethod) return byExactMethod;

    const byExactName = accountsList.find(acc => normalizeLabel(acc.name) === target);
    if (byExactName) return byExactName;

    const byMethodContains = accountsList.find(acc => normalizeLabel(acc.payment_method_name).includes('numerario'));
    if (byMethodContains) return byMethodContains;

    const byCashType = accountsList.find(acc => acc.type === 'cash');
    if (byCashType) return byCashType;

    return accountsList[0] || null;
  }, [normalizeLabel]);

  const dedupedPaymentAccounts = useMemo(() => {
    if (!paymentAccounts.length) return [];
    const byCanonical = new Map();

    paymentAccounts.forEach((account) => {
      const canonical = normalizeLabel(account.payment_method_name || account.name || account.id);
      const key = canonical || account.id;
      const existing = byCanonical.get(key);

      if (!existing) {
        byCanonical.set(key, account);
        return;
      }

      const existingName = String(existing.payment_method_name || existing.name || '');
      const candidateName = String(account.payment_method_name || account.name || '');
      const preferCandidate = existingName.startsWith('.') && !candidateName.startsWith('.');

      if (preferCandidate) {
        byCanonical.set(key, account);
      }
    });

    return Array.from(byCanonical.values());
  }, [paymentAccounts, normalizeLabel]);

  const getDefaultEmployeeId = useCallback((employeesList = employees) => {
    if (!employeesList?.length) return null;

    if (sessionEmployeeId && employeesList.some(emp => emp.id === sessionEmployeeId)) {
      return sessionEmployeeId;
    }

    if (user?.id) {
      const byUserId = employeesList.find(emp => emp.user_id && emp.user_id === user.id);
      if (byUserId) return byUserId.id;
    }

    if (user?.email) {
      const byEmail = employeesList.find(emp => emp.email && emp.email.toLowerCase() === user.email.toLowerCase());
      if (byEmail) return byEmail.id;
    }

    if (user?.full_name) {
      const byName = employeesList.find(emp => normalizeLabel(emp.name) === normalizeLabel(user.full_name));
      if (byName) return byName.id;
    }

    return employeesList[0]?.id || null;
  }, [employees, normalizeLabel, sessionEmployeeId, user?.id, user?.email, user?.full_name]);

  const applyPosDefaults = useCallback(({ clearPayments = false } = {}) => {
    const defaultEmployeeId = getDefaultEmployeeId();
    const defaultCashAccount = getDefaultCashAccount();

    setSelectedEmployee(defaultEmployeeId || null);
    setSelectedPaymentAccount(defaultCashAccount?.id || null);

    if (clearPayments) {
      setPayments([]);
      setPaymentAmount('');
    }
  }, [getDefaultEmployeeId, getDefaultCashAccount]);

  const getPaymentDisplayLabel = useCallback((account) => {
    if (!account) return '';
    const methodLabel = normalizeLabel(account.payment_method_name);
    if (methodLabel === 'numerario') {
      return `${DEFAULT_CASH_PAYMENT_LABEL} • ${account.name}`;
    }
    return account.name;
  }, [normalizeLabel]);

  const summarizeSalePaymentMethods = useCallback((sale) => {
    const labels = [];

    (sale?.payments || []).forEach((payment) => {
      const rawLabel = String(payment?.payment_method_name || payment?.account_name || '').trim();
      if (!rawLabel) return;
      const normalized = normalizeLabel(rawLabel);
      const displayLabel = normalized === 'numerario' ? 'Numerário' : rawLabel.replace(/^\./, '').trim();
      if (!labels.includes(displayLabel)) {
        labels.push(displayLabel);
      }
    });

    const fallbackLabel = String(sale?.payment_method || sale?.payment_method_name || '').trim();
    if (!labels.length && fallbackLabel) {
      labels.push(fallbackLabel.replace(/^\./, '').trim());
    }

    if (!labels.length) return 'Sem forma definida';
    if (labels.length >= 3) return 'Multi-Formas';
    return labels.join(' + ');
  }, [normalizeLabel]);

  const isCashMethodName = useCallback((methodName, fallbackName = '') => {
    const normalized = normalizeLabel(methodName || fallbackName);
    return normalized === 'numerario';
  }, [normalizeLabel]);

  const isCashAccount = useCallback((account) => {
    if (!account) return false;
    return isCashMethodName(account.payment_method_name, account.name);
  }, [isCashMethodName]);

  const loadOverrideAuthorizers = useCallback(async () => {
    try {
      setLoadingOverrideAuthorizers(true);
      const response = await salesAPI.getOverrideAuthorizers();
      const authorizers = Array.isArray(response.data) ? response.data : [];
      setOverrideAuthorizers(authorizers);
      const defaultAuthorizer = authorizers.find((item) => item.id === user?.id) || authorizers[0] || null;
      setOverrideAuthorizerId(defaultAuthorizer?.id || '');
    } catch (error) {
      setOverrideAuthorizers([]);
      setOverrideAuthorizerId('');
      toast.error(getErrorMessage(error, 'Não foi possível carregar os autorizadores disponíveis'));
    } finally {
      setLoadingOverrideAuthorizers(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (marginOverrideOpen && requiresPin) {
      loadOverrideAuthorizers();
    }
  }, [loadOverrideAuthorizers, marginOverrideOpen, requiresPin]);
  
  // View/Edit/Cancel state
  const [selectedSale, setSelectedSale] = useState(null);
  const [viewSaleDialogOpen, setViewSaleDialogOpen] = useState(false);
  const [editSaleDialogOpen, setEditSaleDialogOpen] = useState(false);
  const [editSaleForm, setEditSaleForm] = useState(null);
  const [salesPage, setSalesPage] = useState(1);
  const [cancelledSalesPage, setCancelledSalesPage] = useState(1);
  const [editSaleJustification, setEditSaleJustification] = useState('');
  const [editSalePaymentAccount, setEditSalePaymentAccount] = useState(null);
  const [editSalePaymentAmount, setEditSalePaymentAmount] = useState('');
  const [confirmEditDialogOpen, setConfirmEditDialogOpen] = useState(false);
  const [cancelSaleDialogOpen, setCancelSaleDialogOpen] = useState(false);
  const [cancelSaleReason, setCancelSaleReason] = useState('');
  const [cancelSaleNotes, setCancelSaleNotes] = useState('');
  const [revertStockOnCancel, setRevertStockOnCancel] = useState(true);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  
  // Print Dialog State (P2.1 - Impressão POS + A4)
  const [printDialogOpen, setPrintDialogOpen] = useState(false);

  const canFullyEditSelectedSale = Boolean(selectedSale && selectedSale.status !== 'cancelled' && canManageSaleEdits);
  const [completedSale, setCompletedSale] = useState(null);
  const [printValidationState, setPrintValidationState] = useState({
    open: false,
    warnings: [],
    blob: null,
    filename: '',
    sale: null,
    docType: null,
  });
  
  // Fiscal Dialog State (VD vs Factura)
  const [fiscalDialogOpen, setFiscalDialogOpen] = useState(false);

  useEffect(() => {
    setSelectedSaleIds([]);
  }, [activeSalesTab, sales, cancelledSales]);

  // ===========================================================================
  // BARCODE SCANNER - P7
  // ===========================================================================
  
  // Handle barcode scan result
  const handleBarcodeScan = useCallback((result) => {
    if (!result.found) return;
    
    // Validate location is selected
    if (!selectedLocation) {
      toast.error(t('salesPage.selectLocationBeforeScan'));
      return;
    }
    
    // Only process cart_item type results
    if (result.cart_item) {
      const cartItem = result.cart_item;
      
      // Check if trackable item is already in cart
      if (cartItem.is_trackable && cartItem.item_id) {
        if (cart.some(c => c.item_id === cartItem.item_id)) {
          toast.warning('Este item já está no carrinho', {
            description: cartItem.serial_number
          });
          return;
        }
        
        // Add trackable item directly
        setCart(prev => [...prev, cartItem]);
        
      } else {
        // Non-trackable: increment quantity if already in cart
        const existingIndex = cart.findIndex(c => c.product_id === cartItem.product_id && !c.item_id);
        
        if (existingIndex >= 0) {
          const existing = cart[existingIndex];
          if (existing.quantity >= (cartItem.max_quantity || 999)) {
            toast.error(t('salesPage.insufficientStock'));
            return;
          }
          setCart(prev => prev.map((item, i) => 
            i === existingIndex ? { ...item, quantity: item.quantity + 1 } : item
          ));
        } else {
          setCart(prev => [...prev, cartItem]);
        }
      }
    }
  }, [selectedLocation, cart]);
  
  // Initialize barcode scanner (active when POS dialog is open)
  const { scanning, lastScan } = useBarcodeScanner({
    onScan: handleBarcodeScan,
    locationId: selectedLocation?.id,
    enabled: dialogOpen,
    mode: 'sale'
  });

  const loadDefaultClient = useCallback(async () => {
    try {
      const res = await clientsAPI.getDefault();
      setDefaultClient(res.data);
      setSelectedClient((currentClient) => currentClient || res.data);
    } catch (err) {
      console.error('Erro ao carregar cliente default', err);
    }
  }, []);

  // Load default client (Cliente Indiferenciado)
  useEffect(() => {
    loadDefaultClient();
  }, [loadDefaultClient]);

  // Search clients by query
  useEffect(() => {
    const searchClients = async () => {
      if (!clientSearchQuery || clientSearchQuery.trim().length < 1) {
        setClientResults([]);
        return;
      }
      try {
        const res = await clientsAPI.search(clientSearchQuery.trim(), 10);
        setClientResults(res.data || []);
      } catch (err) {
        console.error('Erro ao pesquisar clientes', err);
      }
    };

    const timer = setTimeout(searchClients, 250);
    return () => clearTimeout(timer);
  }, [clientSearchQuery]);
  
  // Load last used location from sessionStorage
  useEffect(() => {
    if (!locations.length) return;
    if (selectedLocation && locations.some((location) => location.id === selectedLocation.id)) {
      return;
    }

    const resolvedLocationId = resolveHandleOrLegacyId({
      cleanValue: requestedLocationHandle,
      legacyValue: requestedLegacyLocationId,
      handleToId: locationHandleMaps.handleToId,
    });

    const savedLocationId = readLastLocationId();
    const initialLocationId = resolvedLocationId || savedLocationId;
    const initialLocation = (initialLocationId ? locations.find((location) => location.id === initialLocationId) : null)
      || locations.find((location) => location.type === 'store')
      || locations[0];

    if (initialLocation) {
      setSelectedLocation(initialLocation);
      writeLastLocationId(initialLocation.id);
    }
  }, [locationHandleMaps.handleToId, locations, requestedLegacyLocationId, requestedLocationHandle, selectedLocation]);
  
  // Save location to sessionStorage when changed
  // Sync tab state FROM URL only on initial load or external URL changes
  // This effect should NOT run when activeSalesTab changes internally
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    // Only sync from URL if there's an explicit tab parameter
    if (!tabFromUrl) return;
    
    const normalizedTab = tabFromUrl === 'cash'
      ? 'cash'
      : (tabFromUrl === 'cancelled' && canEdit ? 'cancelled' : 'sales');

    // Only update if the URL tab differs from current state
    // Use a ref check to avoid circular updates
    setActiveSalesTab((currentTab) => {
      if (currentTab !== normalizedTab) {
        return normalizedTab;
      }
      return currentTab;
    });
  }, [canEdit, searchParamsString]);

  const linkedSaleId = searchParams.get('sale') || searchParams.get('sale_id');
  const linkedQuoteId = searchParams.get('quote') || searchParams.get('from_quote_id');
  const linkedOpenSession = searchParams.get('open_session');

  useEffect(() => {
    if (skipSalesFilterHydrationRef.current) {
      if (!requestedSalesFilterLocationHandle && !requestedLegacySalesLocationId) {
        skipSalesFilterHydrationRef.current = false;
      }
      return;
    }
    if ((!requestedSalesFilterLocationHandle && !requestedLegacySalesLocationId) || !locations.length || filters.location_id) return;
    const resolvedLocationId = resolveHandleOrLegacyId({
      cleanValue: requestedSalesFilterLocationHandle,
      legacyValue: requestedLegacySalesLocationId,
      handleToId: locationHandleMaps.handleToId,
    });

    if (resolvedLocationId) {
      setFilters({ location_id: resolvedLocationId });
    }
  }, [filters.location_id, locationHandleMaps.handleToId, locations.length, requestedLegacySalesLocationId, requestedSalesFilterLocationHandle, setFilters]);

  const handleSalesFilterLocationChange = useCallback((value) => {
    if (!value || value === 'all') {
      skipSalesFilterHydrationRef.current = true;
      const params = new URLSearchParams(searchParams);
      params.delete('sales_location');
      params.delete('sales_location_id');
      setSearchParams(params, { replace: true });
      setFilters({ location_id: null });
      return;
    }

    skipSalesFilterHydrationRef.current = false;
    setFilters({ location_id: value });
  }, [searchParams, setFilters, setSearchParams]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.period && filters.period !== 'all') params.set('period', filters.period);
    if (filters.period === 'custom') {
      if (filters.custom_start) params.set('custom_start', filters.custom_start);
      if (filters.custom_end) params.set('custom_end', filters.custom_end);
    }
    applyCleanOrLegacyParam({
      params,
      cleanKey: 'location',
      legacyKey: 'location_id',
      value: selectedLocation?.id,
      idToHandle: locationHandleMaps.idToHandle,
    });
    applyCleanOrLegacyParam({
      params,
      cleanKey: 'sales_location',
      legacyKey: 'sales_location_id',
      value: filters.location_id,
      idToHandle: locationHandleMaps.idToHandle,
    });
    if (filters.employee_id) params.set('employee_id', filters.employee_id);
    if (filters.status) params.set('status', filters.status);
    if (filters.payment_account_id) params.set('payment_account_id', filters.payment_account_id);
    params.set('tab', activeSalesTab);
    if (linkedSaleId) params.set('sale', linkedSaleId);
    if (linkedQuoteId) params.set('quote', linkedQuoteId);
    if (linkedOpenSession) params.set('open_session', linkedOpenSession);
    const nextQueryString = params.toString();
    if (nextQueryString !== searchParamsString) {
      setSearchParams(params, { replace: true });
    }
  }, [filters, activeSalesTab, linkedSaleId, linkedQuoteId, linkedOpenSession, locationHandleMaps.idToHandle, searchParamsString, selectedLocation?.id, setSearchParams]);

  const handleLocationChange = (locationId) => {
    const loc = locations.find(l => l.id === locationId);
    if (loc) {
      setSelectedLocation(loc);
      writeLastLocationId(locationId);
      // Clear cart when location changes to avoid inconsistency
      if (cart.length > 0) {
        toast.warning('Carrinho limpo devido a mudança de localização');
        setCart([]);
      }

      if (dialogOpen) {
        applyPosDefaults({ clearPayments: true });
      }
    }
  };

  const loadPaymentAccounts = useCallback(async ({ silent = true } = {}) => {
    const response = await safeRequest(() => settingsAPI.listActivePaymentAccounts(), { data: [] });
    const accounts = response.data || [];
    setPaymentAccounts(accounts);

    const defaultCash = getDefaultCashAccount(accounts);
    if (defaultCash) {
      setSelectedPaymentAccount((current) => current || defaultCash.id);
    }

    if (!silent && !accounts.length) {
      toast.error('Não foi possível carregar as contas activas de pagamento');
    }

    return accounts;
  }, [getDefaultCashAccount]);

  const loadPosProductsAvailability = useCallback(async (searchTerm = '') => {
    if (!selectedLocation?.id) {
      setProducts([]);
      return;
    }

    setProductAvailabilityLoading(true);
    try {
      const response = await inventoryAPI.listPosAvailability({
        location_id: selectedLocation.id,
        search: searchTerm?.trim() || undefined,
        limit: 120
      });
      setProducts(response.data?.products || []);
    } catch (error) {
      toast.error('Erro ao carregar disponibilidade real de produtos');
      setProducts([]);
    } finally {
      setProductAvailabilityLoading(false);
    }
  }, [selectedLocation?.id]);

  useEffect(() => {
    if (!dialogOpen || !selectedLocation?.id) return;
    const timer = setTimeout(() => {
      loadPosProductsAvailability(productSearchQuery);
    }, 250);

    return () => clearTimeout(timer);
  }, [dialogOpen, selectedLocation?.id, productSearchQuery, loadPosProductsAvailability]);

  useEffect(() => {
    if (!dialogOpen) return;
    applyPosDefaults();
  }, [dialogOpen, selectedLocation?.id, applyPosDefaults]);

  const loadSales = useCallback(async () => {
    try {
      if (filters.period === 'custom' && (!filters.custom_start || !filters.custom_end)) {
        setSales([]);
        return;
      }

      // Build params from filters
      const params = {
        period: filters.period || 'all',
      };
      if (filters.location_id) params.location_id = filters.location_id;
      if (filters.employee_id) params.employee_id = filters.employee_id;
      if (filters.status) params.status_filter = filters.status;
      if (filters.payment_account_id) params.payment_account_id = filters.payment_account_id;
      if (filters.period === 'custom') {
        if (filters.custom_start) params.start_date = buildMaputoRangeBoundary(filters.custom_start, 'start');
        if (filters.custom_end) params.end_date = buildMaputoRangeBoundary(filters.custom_end, 'end');
      }
      
      const response = await safeRequest(() => salesAPI.list(params), { data: [] }, { telemetry: { module: 'sales', endpoint: 'sales.list', onEvent: captureFetchTelemetry } });
      setSales(response.data || []);
    } catch (error) {
      toast.error(t('salesPage.loadError'));
    }
  }, [captureFetchTelemetry, filters, t]);

  const getSaleItemsSnapshot = useCallback((sale) => {
    if (Array.isArray(sale?.items) && sale.items.length > 0) {
      return sale.items;
    }
    return [];
  }, []);

  const getSaleSkuSummary = useCallback((sale) => {
    const items = getSaleItemsSnapshot(sale);
    const skuCandidates = items
      .map((item) => item?.sku || item?.product_sku || item?.product_code || item?.kit_code || item?.code)
      .filter(Boolean)
      .map((value) => String(value).trim())
      .filter(Boolean);

    const uniqueSku = [...new Set(skuCandidates)];
    if (!uniqueSku.length) {
      return 'Sem SKU';
    }

    if (uniqueSku.length === 1) {
      return uniqueSku[0];
    }

    const preview = uniqueSku.slice(0, 2).join(', ');
    return uniqueSku.length > 2 ? `${preview} +${uniqueSku.length - 2}` : preview;
  }, [getSaleItemsSnapshot]);

  const getSaleQuantityTotal = useCallback((sale) => {
    const items = getSaleItemsSnapshot(sale);
    const quantity = items.reduce((sum, item) => sum + Number(item?.quantity || 0), 0);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
  }, [getSaleItemsSnapshot]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      resetFetchTelemetry();
      const salesPromise = loadSales();
      const [employeesRes, locationsRes, vatConfigRes, sessionRes] = await Promise.all([
        safeRequest(() => settingsAPI.listActiveEmployees(), { data: [] }, { telemetry: { module: 'sales', endpoint: 'settings.active-employees', onEvent: captureFetchTelemetry } }),
        safeRequest(() => settingsAPI.listLocationsByType('store'), { data: [] }, { telemetry: { module: 'sales', endpoint: 'settings.store-locations', onEvent: captureFetchTelemetry } }),
        safeRequest(() => vatAPI.getConfig(), { data: { default_rate: 16.0, default_regime: 'inclusive' } }, { telemetry: { module: 'sales', endpoint: 'vat.config', onEvent: captureFetchTelemetry } }),
        safeRequest(() => authAPI.getSession(), { data: null }, { telemetry: { module: 'sales', endpoint: 'auth.session', onEvent: captureFetchTelemetry } })
      ]);
      setEmployees(employeesRes.data || []);
      setSessionEmployeeId(sessionRes?.data?.employee_id || null);
      const saleLocations = (locationsRes.data || []).filter(
        (location) => location.is_deleted !== true && location.is_active !== false && location.is_hidden !== true
      );
      setLocations(saleLocations);

      const defaultEmployee = (employeesRes.data || []).find(emp => emp.id === sessionRes?.data?.employee_id)
        || (employeesRes.data || []).find(emp => emp.user_id && emp.user_id === user?.id)
        || (employeesRes.data || []).find(emp => emp.email && user?.email && emp.email.toLowerCase() === user.email.toLowerCase())
        || (employeesRes.data || []).find(emp => normalizeLabel(emp.name) === normalizeLabel(user?.full_name))
        || (employeesRes.data || [])[0];
      if (defaultEmployee?.id) {
        setSelectedEmployee(defaultEmployee.id);
      }

      // VAT configuration
      if (vatConfigRes.data) {
        setVatConfig(vatConfigRes.data);
        setSaleVatRegime(vatConfigRes.data.default_regime || 'inclusive');
      }
      if ([employeesRes, locationsRes, vatConfigRes, sessionRes].some((result) => result.status === 'fallback')) {
        toast.info(t('salesPage.partialData'));
      }
      await salesPromise;
      skipNextSalesReloadRef.current = true;
      void loadPaymentAccounts({ silent: true });
    } catch (error) {
      toast.error(t('salesPage.loadError'));
    } finally {
      setLoading(false);
    }
  }, [captureFetchTelemetry, loadPaymentAccounts, resetFetchTelemetry, t, user?.email, user?.full_name, user?.id]);

  // Initial data load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load sales when filters change
  useEffect(() => {
    if (!loading) {
      if (skipNextSalesReloadRef.current) {
        skipNextSalesReloadRef.current = false;
        return;
      }
      loadSales();
    }
  }, [filters, loadSales, loading]);

  const openLinkedSaleFromUrl = useCallback(async () => {
    const linkedSaleId = searchParams.get('sale') || searchParams.get('sale_id');
    const linkedQuoteId = searchParams.get('quote') || searchParams.get('from_quote_id');
    const linkedProjectId = searchParams.get('from_project_id');
    if (!linkedSaleId || loading) return;

    let fullSaleData = null;

    try {
      fullSaleData = sales.find((sale) => sale.id === linkedSaleId || sale.sale_number === linkedSaleId) || null;
      if (!fullSaleData) {
        const res = await salesAPI.get(linkedSaleId);
        fullSaleData = res.data;
      }
    } catch {
      fullSaleData = sales.find((sale) => sale.id === linkedSaleId) || null;
    }

    if (!fullSaleData) return;

    if (activeSalesTab !== 'sales') {
      setActiveSalesTab('sales');
    }

    setSelectedSale(fullSaleData);
    setViewSaleDialogOpen(true);

    if (linkedQuoteId) {
      toast.success(t('salesPage.saleFromQuote', { reference: linkedQuoteId }));
    } else if (linkedProjectId) {
      toast.success(t('salesPage.saleFromProject', { reference: fullSaleData.source_project_number || linkedProjectId }));
    }

    const params = new URLSearchParams(searchParamsString);
    params.delete('sale');
    params.delete('sale_id');
    params.delete('sale_number');
    params.delete('quote');
    params.delete('from_quote_id');
    params.delete('from_project_id');
    setSearchParams(params, { replace: true });
  }, [activeSalesTab, loading, sales, searchParams, searchParamsString, setSearchParams, t]);

  useEffect(() => {
    openLinkedSaleFromUrl();
  }, [openLinkedSaleFromUrl]);

  const loadActiveCashSession = useCallback(async (locationId = selectedLocation?.id) => {
    if (!locationId) {
      setActiveCashSession(null);
      return;
    }

    setLoadingCashSession(true);
    try {
      const response = await cashAPI.getActiveSession(locationId);
      setActiveCashSession(response.data || null);
    } catch (error) {
      if (error?.response?.status !== 404) {
        toast.error(getErrorMessage(error, 'Erro ao carregar sessão de caixa'));
      }
      setActiveCashSession(null);
    } finally {
      setLoadingCashSession(false);
    }
  }, [selectedLocation?.id]);

  useEffect(() => {
    loadActiveCashSession();
  }, [loadActiveCashSession]);

  const loadCancelledSales = useCallback(async () => {
    try {
      const response = await safeRequest(() => salesAPI.listCancelled(), { data: [] }, { telemetry: { module: 'sales', endpoint: 'sales.cancelled', onEvent: captureFetchTelemetry } });
      setCancelledSales(response.data || []);
    } catch (error) {
      toast.error(t('salesPage.loadError'));
    }
  }, [captureFetchTelemetry, t]);

  const handleSalesTabChange = useCallback((nextTab) => {
    const normalizedTab = nextTab === 'cash'
      ? 'cash'
      : (nextTab === 'cancelled' && canEdit ? 'cancelled' : 'sales');

    setActiveSalesTab(normalizedTab);
    if (normalizedTab === 'cancelled') {
      loadCancelledSales();
    }
  }, [canEdit, loadCancelledSales]);

  const handleClearOpenCashRequest = useCallback(() => {
    if (searchParams.get('open_session') !== '1') return;
    const params = new URLSearchParams(searchParams);
    params.delete('open_session');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const handlePurgeCancelledSales = async () => {
    const saleId = purgeCancelledDialog.sale?.id;
    try {
      setAdminActionLoading(true);
      const response = await salesAPI.purgeCancelled({
        ids: saleId ? [saleId] : [],
        reason: 'Limpeza permanente de vendas canceladas',
      });
      toast.success(t('salesPage.purgedCancelledSales', { count: response.data?.purged || 0 }));
      setPurgeCancelledDialog({ open: false, sale: null });
      await Promise.all([loadSales(), loadCancelledSales(), loadActiveCashSession()]);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao eliminar vendas canceladas'));
    } finally {
      setAdminActionLoading(false);
    }
  };

  // Filter products
  const filteredProducts = useMemo(() => {
    const available = products.filter(
      p => (Number(p.total_available ?? p.current_stock ?? 0) > 0) && !p.is_deleted
    );
    if (!productSearchQuery) return available;
    const query = productSearchQuery.toLowerCase();
    return available.filter(p => 
      p.name?.toLowerCase().includes(query) ||
      p.sku?.toLowerCase().includes(query) ||
      p.kit_code?.toLowerCase().includes(query) ||
      p.brand?.toLowerCase().includes(query)
    );
  }, [products, productSearchQuery]);

  const getSelectedLocationAvailable = (product) => {
    const qty = Number(product?.selected_location_available ?? product?.current_stock ?? 0);
    if (!Number.isFinite(qty)) return 0;
    return Math.max(0, qty);
  };

  // Load available items for trackable product - filtered by location
  const loadAvailableItems = async (product) => {
    if (!selectedLocation) {
      toast.error(t('salesPage.selectLocationFirstShort'));
      return;
    }
    
    setLoadingItems(true);
    try {
      const response = await itemsAPI.getAvailableForSale(product.id, selectedLocation.id);
      const items = response.data || [];
      setAvailableItems(items);
      
      if (items.length === 0) {
        toast.warning(`Nenhum item disponível em ${selectedLocation.name}`);
      }
    } catch (error) {
      toast.error('Erro ao carregar items disponíveis');
      setAvailableItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  // Handle product selection
  const handleProductSelect = async (product) => {
    // Validate location is selected
    if (!selectedLocation) {
      toast.error(t('salesPage.selectLocationBeforeProducts'));
      return;
    }

    const selectedLocationAvailable = getSelectedLocationAvailable(product);
    if (selectedLocationAvailable <= 0) {
      toast.error(`Sem stock disponível em ${selectedLocation?.name || 'localização seleccionada'}`);
      return;
    }
    
    setProductSearchOpen(false);
    setProductSearchQuery('');
    
    if (product.is_trackable) {
      setPendingProduct(product);
      await loadAvailableItems(product);
      setItemSelectDialogOpen(true);
    } else {
      addNonTrackableToCart(product);
    }
  };

  const addNonTrackableToCart = (product) => {
    const maxAvailableInLocation = getSelectedLocationAvailable(product);

    if (maxAvailableInLocation <= 0) {
      toast.error(`Sem stock disponível em ${selectedLocation?.name || 'localização seleccionada'}`);
      return;
    }

    const existingItem = cart.find(item => item.product_id === product.id && !item.item_id);
    
    if (existingItem) {
      if (existingItem.quantity >= maxAvailableInLocation) {
        toast.error(t('salesPage.insufficientStock'));
        return;
      }
      setCart(cart.map(item => 
        item.product_id === product.id && !item.item_id
          ? { ...item, quantity: item.quantity + 1, max_quantity: maxAvailableInLocation }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        description: product.pos_description || '',
        pos_description_fields: product.pos_description_fields || [],
        item_type: product.is_kit ? 'kit' : 'product',
        is_kit: !!product.is_kit,
        kit_id: product.kit_id || null,
        kit_code: product.kit_code || null,
        kit_component_count: product.kit_component_count || 0,
        quantity: 1,
        unit_price: product.unit_price,
        discount: 0,
        discount_percent: 0,
        is_trackable: false,
        item_id: null,
        serial_number: null,
        max_quantity: maxAvailableInLocation,
        cost_mode: product.cost_mode,
        avg_cost: product.avg_cost,
        last_cost: product.last_cost,
        cost_price: product.cost_price
      }]);
    }
    toast.success(`${product.name} adicionado`);
  };

  const handleItemSelect = (item) => {
    if (!pendingProduct?.id) {
      toast.error('Produto pendente inválido');
      return;
    }

    if (cart.some(cartItem => cartItem.item_id === item.id)) {
      toast.error('Este item já está no carrinho');
      return;
    }

    setCart([...cart, {
      product_id: pendingProduct.id,
      product_name: pendingProduct.name,
      description: pendingProduct.pos_description || '',
      pos_description_fields: pendingProduct.pos_description_fields || [],
      item_type: pendingProduct.is_kit ? 'kit' : 'product',
      is_kit: !!pendingProduct.is_kit,
      kit_id: pendingProduct.kit_id || null,
      kit_code: pendingProduct.kit_code || null,
      kit_component_count: pendingProduct.kit_component_count || 0,
      quantity: 1,
      unit_price: pendingProduct.unit_price,
      discount: 0,
      discount_percent: 0,
      is_trackable: true,
      item_id: item.id,
      serial_number: item.imei1 || item.serial_number,
      imei1: item.imei1,
      condition: item.condition,
      location: item.location,
      tracked_purchase_price: item.purchase_price,
      cost_mode: pendingProduct.cost_mode,
      avg_cost: pendingProduct.avg_cost,
      last_cost: pendingProduct.last_cost,
      cost_price: pendingProduct.cost_price
    }]);
    
    toast.success(`${pendingProduct.name} (${item.imei1 || item.serial_number}) adicionado`);
    setItemSelectDialogOpen(false);
    setPendingProduct(null);
    setSelectedItemForSale(null);
  };

  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  const updateQuantity = (index, newQuantity) => {
    const item = cart[index];
    if (item.is_trackable) return;
    
    const product = products.find(p => p.id === item.product_id);
    const maxAvailable = Number(item.max_quantity ?? getSelectedLocationAvailable(product));
    if (newQuantity > maxAvailable) {
      toast.error(t('salesPage.insufficientStock'));
      return;
    }
    
    if (newQuantity < 1) {
      removeFromCart(index);
      return;
    }
    
    setCart(cart.map((item, i) => 
      i === index ? {...item, quantity: newQuantity} : item
    ));
  };

  const updateItemDiscount = (index, discount, isPercent = false) => {
    setCart(cart.map((item, i) => {
      if (i !== index) return item;
      if (isPercent) {
        return {...item, discount_percent: parseFloat(discount) || 0};
      }
      return {...item, discount: parseFloat(discount) || 0};
    }));
  };

  // Payment handlers
  const addPayment = () => {
    if (!selectedPaymentAccount || !paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error(t('salesPage.selectAccountAndAmount'));
      return;
    }
    
    const account = paymentAccounts.find(a => a.id === selectedPaymentAccount);
    if (!account) return;
    
    // Check if account already added
    if (payments.some(p => p.account_id === selectedPaymentAccount)) {
      toast.error('Esta conta já foi adicionada');
      return;
    }

    const amount = parseFloat(paymentAmount);
    const projectedPayments = [...payments, {
      account_id: account.id,
      account_name: account.name,
      payment_method_id: account.payment_method_id,
      payment_method_name: account.payment_method_name,
      display_label: getPaymentDisplayLabel(account),
      amount
    }];
    const validation = getPaymentValidationState(projectedPayments, { requireSettlement: false });

    if (!validation.isValid) {
      toast.error(validation.message);
      return;
    }
    
    setPayments(projectedPayments);
    
    setSelectedPaymentAccount(null);
    setPaymentAmount('');
  };

  const removePayment = (index) => {
    setPayments(payments.filter((_, i) => i !== index));
  };

  const updatePaymentAmount = (index, amount) => {
    const parsedAmount = parseFloat(amount) || 0;
    const nextPayments = payments.map((p, i) =>
      i === index ? { ...p, amount: parsedAmount } : p
    );

    const validation = getPaymentValidationState(nextPayments, { requireSettlement: false });
    if (!validation.isValid) {
      toast.error(validation.message);
      return;
    }

    setPayments(nextPayments);
  };

  // Calculations
  const calculateSubtotal = () => cart.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  
  const calculateItemsDiscount = () => cart.reduce((sum, item) => {
    let itemDiscount = item.discount || 0;
    if (item.discount_percent > 0) {
      itemDiscount += (item.quantity * item.unit_price) * (item.discount_percent / 100);
    }
    return sum + itemDiscount;
  }, 0);
  
  const calculateSaleDiscount = () => {
    const afterItemsDiscount = calculateSubtotal() - calculateItemsDiscount();
    let discount = saleDiscountValue || 0;
    if (saleDiscountPercent > 0) {
      discount += afterItemsDiscount * (saleDiscountPercent / 100);
    }
    return discount;
  };
  
  const calculateTotalDiscount = () => calculateItemsDiscount() + calculateSaleDiscount();

  const getItemNetTotal = (item) => {
    const gross = (Number(item.quantity || 0) * Number(item.unit_price || 0));
    const discountValue = Number(item.discount || 0);
    const discountPct = Number(item.discount_percent || 0);
    const pctValue = gross * (discountPct / 100);
    return Math.max(gross - discountValue - pctValue, 0);
  };

  const resolveCartItemCost = (item) => {
    const trackedPurchase = Number(item.tracked_purchase_price || 0);
    const avgCost = Number(item.avg_cost || 0);
    const lastCost = Number(item.last_cost || 0);
    const legacyCost = Number(item.cost_price || 0);

    if (trackedPurchase > 0) return { unitCost: trackedPurchase, confidence: 'ok', basis: 'item_purchase_price' };
    if (avgCost > 0) return { unitCost: avgCost, confidence: 'ok', basis: 'avg_cost' };
    if (lastCost > 0) return { unitCost: lastCost, confidence: 'partial', basis: 'last_cost' };
    if (legacyCost > 0) return { unitCost: legacyCost, confidence: 'partial', basis: 'legacy_cost_price' };
    return { unitCost: 0, confidence: 'missing', basis: 'missing' };
  };

  const getCartMarginPreview = () => {
    let totalCost = 0;
    let hasNotReliable = false;

    const lines = cart.map((item) => {
      const netTotal = getItemNetTotal(item);
      const costInfo = resolveCartItemCost(item);
      const lineCost = costInfo.unitCost > 0 ? (costInfo.unitCost * Number(item.quantity || 0)) : 0;
      const lineProfit = costInfo.unitCost > 0 ? (netTotal - lineCost) : null;
      const lineMarginPct = (lineProfit !== null && netTotal > 0) ? ((lineProfit / netTotal) * 100) : null;

      totalCost += lineCost;
      if (costInfo.confidence !== 'ok') hasNotReliable = true;

      return {
        lineCost,
        lineProfit,
        lineMarginPct,
        costInfo,
      };
    });

    const total = calculateTotal();
    const totalProfit = total - totalCost;
    const totalMarginPct = total > 0 ? ((totalProfit / total) * 100) : null;

    return {
      lines,
      totalCost,
      totalProfit,
      totalMarginPct,
      marginConfidence: hasNotReliable ? 'not_reliable' : 'ok'
    };
  };
  
  // VAT Calculation Functions
  const calculateVAT = (price, rate, regime) => {
    const vatRate = rate / 100;
    if (regime === 'exempt') {
      return { base: price, vat: 0, total: price };
    }
    if (regime === 'inclusive') {
      const base = price / (1 + vatRate);
      return { base: Math.round(base * 100) / 100, vat: Math.round((price - base) * 100) / 100, total: price };
    }
    // exclusive
    const vat = price * vatRate;
    return { base: price, vat: Math.round(vat * 100) / 100, total: Math.round((price + vat) * 100) / 100 };
  };

  const editSaleTotals = useMemo(() => {
    if (!editSaleForm) {
      return { base: 0, vat: 0, total: 0 };
    }

    const draftItems = Array.isArray(editSaleForm.items) ? editSaleForm.items : [];
    let totalBase = 0;
    let totalVat = 0;

    draftItems.forEach((item) => {
      const quantity = Math.max(1, Number(item.quantity) || 1);
      const unitPrice = Number(item.unit_price) || 0;
      const discountValue = Number(item.discount) || 0;
      const discountPercent = Number(item.discount_percent) || 0;
      const lineAmount = quantity * unitPrice;
      const lineDiscount = discountValue + (lineAmount * (discountPercent / 100));
      const lineNet = Math.max(lineAmount - lineDiscount, 0);
      const vatCalc = calculateVAT(lineNet, Number(item.vat_rate) || vatConfig.default_rate || 16, item.vat_regime || 'inclusive');
      totalBase += vatCalc.base;
      totalVat += vatCalc.vat;
    });

    let saleDiscount = Math.max(Number(editSaleForm.sale_discount) || 0, 0);
    const saleDiscountPercent = Math.max(Number(editSaleForm.sale_discount_percent) || 0, 0);
    const grossBeforeSaleDiscount = totalBase + totalVat;
    if (saleDiscountPercent > 0) {
      saleDiscount += grossBeforeSaleDiscount * (saleDiscountPercent / 100);
    }

    const appliedDiscount = Math.min(saleDiscount, grossBeforeSaleDiscount);
    if (appliedDiscount > 0 && grossBeforeSaleDiscount > 0) {
      const ratio = appliedDiscount / grossBeforeSaleDiscount;
      totalBase -= totalBase * ratio;
      totalVat -= totalVat * ratio;
    }

    return {
      base: Math.round(totalBase * 100) / 100,
      vat: Math.round(totalVat * 100) / 100,
      total: Math.round((totalBase + totalVat) * 100) / 100,
    };
  }, [editSaleForm, vatConfig.default_rate]);

  const editSalePaymentsTotal = useMemo(
    () => (editSaleForm?.payments || []).reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0),
    [editSaleForm]
  );

  const availableEditPaymentAccounts = useMemo(
    () => dedupedPaymentAccounts.filter((account) => !(editSaleForm?.payments || []).some((payment) => payment.account_id === account.id)),
    [dedupedPaymentAccounts, editSaleForm]
  );
  
  const calculateCartVAT = () => {
    let totalBase = 0;
    let totalVat = 0;
    const rate = vatConfig.default_rate || 16.0;
    
    cart.forEach(item => {
      const itemNet = (item.unit_price * item.quantity) - (item.discount || 0) - (item.unit_price * item.quantity * (item.discount_percent || 0) / 100);
      const vatCalc = calculateVAT(itemNet, rate, saleVatRegime);
      totalBase += vatCalc.base;
      totalVat += vatCalc.vat;
    });
    
    // Apply sale discount proportionally
    const saleDisc = calculateSaleDiscount();
    if (saleDisc > 0 && (totalBase + totalVat) > 0) {
      const ratio = saleDisc / (totalBase + totalVat);
      totalBase -= totalBase * ratio;
      totalVat -= totalVat * ratio;
    }
    
    return { 
      base: Math.round(totalBase * 100) / 100, 
      vat: Math.round(totalVat * 100) / 100, 
      total: Math.round((totalBase + totalVat) * 100) / 100 
    };
  };
  
  const calculateTotal = () => {
    const vatTotals = calculateCartVAT();
    return vatTotals.total;
  };
  const calculateTotalPayments = (paymentList = payments) => (paymentList || []).reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0);
  const getPaymentSummary = (paymentList = payments, totalOverride = null) => {
    const total = totalOverride == null ? calculateTotal() : (Number(totalOverride) || 0);
    const normalizedPayments = Array.isArray(paymentList) ? paymentList : [];
    const totalPaid = calculateTotalPayments(normalizedPayments);
    const cashTotal = normalizedPayments.reduce((sum, payment) => {
      if (!isCashMethodName(payment.payment_method_name, payment.account_name)) {
        return sum;
      }
      return sum + (Number(payment.amount) || 0);
    }, 0);
    const nonCashTotal = Math.max(totalPaid - cashTotal, 0);
    const hasCashPayment = cashTotal > 0;
    const cashRequired = Math.max(total - nonCashTotal, 0);
    const remaining = Math.max(total - totalPaid, 0);
    const nonCashOverpay = Math.max(nonCashTotal - total, 0);
    const change = hasCashPayment ? Math.max(cashTotal - cashRequired, 0) : 0;

    return {
      total,
      totalPaid,
      cashTotal,
      nonCashTotal,
      cashRequired,
      remaining,
      nonCashOverpay,
      change,
      hasCashPayment,
      cashOnly: normalizedPayments.length > 0 && normalizedPayments.every((payment) => isCashMethodName(payment.payment_method_name, payment.account_name))
    };
  };
  const getPaymentValidationState = (paymentList = payments, options = {}) => {
    const { requireSettlement = true, totalOverride = null } = options;
    const summary = getPaymentSummary(paymentList, totalOverride);

    if (!paymentList?.length) {
      return {
        ...summary,
        isValid: false,
        code: 'missing',
        message: 'Adicione pelo menos uma forma de pagamento'
      };
    }

    if (summary.nonCashOverpay > 0.009) {
      return {
        ...summary,
        isValid: false,
        code: 'non_cash_overpay',
        message: 'Troco só pode sair do Numerário'
      };
    }

    if (requireSettlement && summary.remaining > 0.009) {
      return {
        ...summary,
        isValid: false,
        code: summary.hasCashPayment ? 'cash_under' : 'exact_required',
        message: summary.hasCashPayment
          ? `Numerário insuficiente para fechar a venda (${summary.totalPaid.toFixed(2)} MT para total ${summary.total.toFixed(2)} MT)`
          : `Pagamento deve ser exato para métodos não numerário (${summary.total.toFixed(2)} MT)`
      };
    }

    return {
      ...summary,
      isValid: true,
      code: 'ok',
      message: ''
    };
  };
  const calculateRemaining = (paymentList = payments) => getPaymentSummary(paymentList).remaining;
  const paymentSummary = getPaymentSummary(payments);

  // Fill remaining payment
  const fillRemainingPayment = () => {
    const remaining = calculateRemaining();
    if (remaining > 0) {
      setPaymentAmount(remaining.toFixed(2));
    }
  };

  const selectedPaymentAccountData = useMemo(
    () => paymentAccounts.find(a => a.id === selectedPaymentAccount) || dedupedPaymentAccounts.find(a => a.id === selectedPaymentAccount) || null,
    [paymentAccounts, dedupedPaymentAccounts, selectedPaymentAccount]
  );
  const isSelectedPaymentCash = selectedPaymentAccountData ? isCashAccount(selectedPaymentAccountData) : false;

  const handleOpenCashFromPos = useCallback(() => {
    const params = new URLSearchParams();
    const locationId = selectedLocation?.id || filters.location_id;
    if (locationId) params.set('location_id', locationId);
    params.set('tab', 'cash');
    params.set('open_session', '1');
    setDialogOpen(false);
    navigate(`/sales?${params.toString()}`);
  }, [selectedLocation?.id, filters.location_id, navigate]);

  // Process sale
  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('Carrinho vazio');
      return;
    }
    
    if (!selectedLocation) {
      toast.error(t('salesPage.selectLocationForSale'));
      return;
    }

    if (!createAsDraft && !activeCashSession?.id) {
      toast.error(`Abra uma sessão de caixa em ${selectedLocation.name} antes de confirmar a venda`);
      handleSalesTabChange('cash');
      return;
    }

    if (!selectedEmployee) {
      toast.error(t('salesPage.selectEmployeeForSale'));
      return;
    }

    if (!selectedClient || !selectedClient.id) {
      toast.error(t('salesPage.selectClientForSale'));
      return;
    }

    if (payments.length === 0) {
      toast.error('Adicione pelo menos uma forma de pagamento');
      return;
    }

    const paymentValidation = getPaymentValidationState(payments, { requireSettlement: false });
    const totalPayments = paymentValidation.totalPaid;
    const changeAmount = paymentValidation.change;

    if (!paymentValidation.isValid) {
      toast.error(paymentValidation.message);
      return;
    }

    const employee = employees.find(e => e.id === selectedEmployee);
    if (!employee) {
      toast.error(t('salesPage.employeeNotFound'));
      return;
    }

    // Calculate VAT for each item
    const vatRate = vatConfig.default_rate || 16.0;
    const itemsWithVat = cart.map(item => {
      const itemNet = (item.unit_price * item.quantity) - (item.discount || 0) - (item.unit_price * item.quantity * (item.discount_percent || 0) / 100);
      const vatCalc = calculateVAT(itemNet, vatRate, saleVatRegime);
      return {
        product_id: item.product_id,
        product_name: item.product_name,
        description: item.description || '',
        pos_description_fields: item.pos_description_fields || [],
        item_type: item.is_kit ? 'kit' : 'product',
        is_kit: !!item.is_kit,
        kit_id: item.kit_id || null,
        kit_code: item.kit_code || null,
        kit_components_snapshot: item.kit_components_snapshot || null,
        quantity: item.quantity,
        unit_price: item.unit_price,
        discount: item.discount || 0,
        discount_percent: item.discount_percent || 0,
        item_id: item.item_id,
        serial_number: item.serial_number,
        // VAT fields
        vat_regime: saleVatRegime,
        vat_rate: vatRate,
        vat_amount: vatCalc.vat,
        base_price: vatCalc.base
      };
    });
    
    const payload = {
      items: itemsWithVat,
      client_id: selectedClient.id,
      // Location - P1.1
      location_id: selectedLocation.id,
      location_name: selectedLocation.name,
      payments: payments,
      payment_method_name: payments?.[0]?.payment_method_name,
      paid_amount: totalPayments,
      change_amount: changeAmount,
      sale_discount: saleDiscountValue || 0,
      sale_discount_percent: saleDiscountPercent || 0,
      // VAT regime
      vat_regime: saleVatRegime,
      employee_id: selectedEmployee,
      employee_name: employee.name,
      seller_id: selectedEmployee,
      seller_name: employee.name,
      notes: saleNotes || undefined,
      as_draft: createAsDraft,
      registered_at: saleRegisteredAt ? new Date(saleRegisteredAt).toISOString() : null
    };

    setProcessing(true);
    try {
      const response = await salesAPI.create(payload);
      
      const createdSale = response.data;
      
      if (createAsDraft) {
        toast.success(t('salesPage.draftCreated'));
      } else {
        // Show fiscal dialog to choose VD or Factura
        setCompletedSale(createdSale);
        setFiscalDialogOpen(true);
        toast.success(t('salesPage.saleCompletedAt', { location: selectedLocation.name }));
      }
      
      resetPOSForm();
      setDialogOpen(false);
      loadSales();
    } catch (error) {
      const warning = error?.response?.data;
      if (warning?.code === 'margin_override_required') {
        setMarginWarning(warning);
        setPendingSalePayload(payload);
        setOverridePin('');
        setOverrideAuthorizerId('');
        setMarginOverrideOpen(true);
        return;
      }
      toast.error(getErrorMessage(error, 'Erro ao processar venda'));
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelMarginOverride = () => {
    setMarginOverrideOpen(false);
    setMarginWarning(null);
    setPendingSalePayload(null);
    setOverridePin('');
    setOverrideAuthorizers([]);
    setOverrideAuthorizerId('');
  };

  const handleConfirmMarginOverride = async () => {
    if (requiresPin && (!overridePin || overridePin.trim().length < 4)) {
      toast.error(t('salesPage.commercialPinRequired'));
      return;
    }
    if (requiresPin && !overrideAuthorizerId) {
      toast.error(t('salesPage.authorizerRequired'));
      return;
    }
    if (!pendingSalePayload) return;

    setOverrideProcessing(true);
    try {
      const response = await salesAPI.create({
        ...pendingSalePayload,
        force_override: true,
        override_pin: requiresPin ? overridePin.trim() : undefined,
        override_authorizer_user_id: requiresPin ? overrideAuthorizerId : undefined,
        override_snapshot: marginWarning
      });

      const createdSale = response.data;

      if (pendingSalePayload.as_draft) {
        toast.success(t('salesPage.draftCreated'));
      } else {
        setCompletedSale(createdSale);
        setFiscalDialogOpen(true);
        toast.success(t('salesPage.saleCompletedAt', { location: pendingSalePayload.location_name }));
        if (createdSale?.override_by_user_name) {
          toast.success(t('salesPage.saleAuthorizedBy', { name: createdSale.override_by_user_name }));
        }
      }

      resetPOSForm();
      setDialogOpen(false);
      loadSales();
      handleCancelMarginOverride();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.code === 'authorizer_required') {
        toast.error(t('salesPage.authorizerRequired'));
        return;
      }
      if (detail?.code === 'authorizer_invalid') {
        toast.error(t('salesPage.authorizerInvalid'));
        return;
      }
      if (detail?.code === 'authorizer_pin_not_configured') {
        toast.error(t('salesPage.authorizerPinNotConfigured'));
        return;
      }
      if (detail?.code === 'approval_required') {
        toast.error(t('salesPage.commercialPinRequired'));
        return;
      }
      if (detail?.code === 'pin_invalid') {
        toast.error(t('salesPage.invalidCommercialPin'));
        return;
      }
      if (detail?.code === 'pin_rate_limit') {
        toast.error(t('salesPage.commercialPinRateLimit'));
        return;
      }
      toast.error(getErrorMessage(error, 'Erro ao processar override'));
    } finally {
      setOverrideProcessing(false);
    }
  };

  const resetPOSForm = () => {
    setCart([]);
    setSelectedClient(defaultClient || null);
    setClientSearchQuery('');
    setSelectedEmployee(getDefaultEmployeeId());
    setPayments([]);
    setSaleDiscountValue(0);
    setSaleDiscountPercent(0);
    setSaleNotes('');
    setCreateAsDraft(false);
    setSaleRegisteredAt('');
    setSelectedPaymentAccount(getDefaultCashAccount()?.id || null);
    setPaymentAmount('');
    // Reset VAT to default
    setSaleVatRegime(vatConfig.default_regime || 'inclusive');
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setClientSearchOpen(false);
    setClientSearchQuery('');
  };

  const handleSelectDefaultClient = () => {
    if (defaultClient) {
      setSelectedClient(defaultClient);
      setClientSearchOpen(false);
      setClientSearchQuery('');
    }
  };

  const handleOpenCreateClient = () => {
    setClientForm(prev => ({
      ...prev,
      name: clientSearchQuery || prev.name
    }));
    setClientCreateOpen(true);
  };

  const handleCreateClient = async () => {
    if (!clientForm.name || clientForm.name.trim().length < 2) {
      toast.error('Nome do cliente é obrigatório');
      return;
    }
    setCreatingClient(true);
    try {
      const res = await clientsAPI.create({
        name: clientForm.name.trim(),
        phone: clientForm.phone || undefined,
        email: clientForm.email || undefined,
        nuit: clientForm.nuit || undefined,
        address: clientForm.address || undefined,
        city: clientForm.city || undefined,
        contact_person: clientForm.contact_person || undefined,
        client_type: clientForm.client_type || 'individual'
      });
      setSelectedClient(res.data);
      setClientCreateOpen(false);
      setClientSearchOpen(false);
      setClientSearchQuery('');
      toast.success('Cliente criado e associado à venda');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erro ao criar cliente'));
    } finally {
      setCreatingClient(false);
    }
  };

  const openNewSale = async () => {
    if (!paymentAccounts.length) {
      const accounts = await loadPaymentAccounts({ silent: false });
      if (!accounts.length) {
        return;
      }
    }
    resetPOSForm();
    applyPosDefaults();
    setDialogOpen(true);
  };

  // View/Edit/Cancel handlers
  const handleViewSale = (sale) => {
    setSelectedSale(sale);
    setViewSaleDialogOpen(true);
  };

  const handleViewPrint = async (docType) => {
    if (!selectedSale) return;
    try {
      const result = await printAPI.renderDocumentPdf({
        doc_type: docType,
        sale_id: selectedSale.id,
        location_id: selectedSale.location_id,
        module_group: 'VENDAS'
      }, { deferOnMissingData: true });

      if (result.success) {
        if (result.hasMissingData) {
          setPrintValidationState({
            open: true,
            warnings: result.warnings || [],
            blob: result.blob,
            filename: result.filename,
            sale: selectedSale,
            docType
          });
          return;
        }
        if (result.usedFallback) {
          toast.warning('Template não configurado - usando modelo antigo', {
            description: result.warningMessage || 'Configure um template no Template Builder para personalizar este documento.',
            duration: 6000
          });
        } else {
          toast.success(t('salesPage.printDocumentOpened'), {
            description: 'Use Ctrl+P para imprimir ou guardar como PDF'
          });
        }
      } else {
        toast.error('Erro ao abrir documento', {
          description: result.error || 'Erro desconhecido'
        });
      }
    } catch (err) {
      toast.error('Erro inesperado ao imprimir');
    }
  };

  const handlePrintCancelledSale = async (sale) => {
    if (!sale?.id) return;
    try {
      const result = await printAPI.renderDocumentPdf({
        doc_type: 'cancelled_sale',
        sale_id: sale.id,
        location_id: sale.location_id,
        module_group: 'VENDAS'
      }, { deferOnMissingData: true });

      if (result?.blob) {
        openPdfBlob(result.blob, result.filename || `ANULADO-${sale.sale_number || sale.id}.pdf`, 'preview');
        toast.success(t('salesPage.printCancelledOpened'));
        return;
      }

      if (!result?.success) {
        toast.error('Erro ao abrir documento anulado', {
          description: result?.error || 'Erro desconhecido'
        });
        return;
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erro inesperado ao imprimir documento anulado'));
    }
  };

  const handleMissingDataPrint = (reason) => {
    if (printValidationState.blob) {
      printAPI.openPdfBlob(printValidationState.blob, printValidationState.filename, 'preview');
      toast.warning('Impressão concluída com dados em falta', {
        description: reason ? `Motivo: ${reason}` : 'Considere completar o perfil do cliente.'
      });
    }
    setPrintValidationState({ open: false, warnings: [], blob: null, filename: '', sale: null, docType: null });
  };

  const handleEditClientFromPrint = () => {
    const target = printValidationState.sale?.client_id ? `/clients?edit=${printValidationState.sale.client_id}` : '/clients';
    setPrintValidationState({ open: false, warnings: [], blob: null, filename: '', sale: null, docType: null });
    setViewSaleDialogOpen(false);
    window.open(target, '_blank');
  };

  const handleOpenEditSale = (sale) => {
    if (sale.status === 'cancelled') {
      toast.error('Vendas canceladas não podem ser editadas');
      return;
    }
    if (!canManageSaleEdits) {
      toast.error('Sem permissão para editar vendas');
      return;
    }
    setSelectedSale(sale);
    setEditSaleForm({
      customer_name: sale.customer_name || '',
      customer_phone: sale.customer_phone || '',
      customer_email: sale.customer_email || '',
      customer_nuit: sale.customer_nuit || '',
      customer_address: sale.customer_address || '',
      customer_city: sale.customer_city || '',
      customer_contact_person: sale.customer_contact_person || '',
      notes: sale.notes || '',
      employee_id: sale.employee_id || '',
      employee_name: sale.employee_name || sale.seller_name || '',
      sale_discount: sale.sale_discount || 0,
      sale_discount_percent: sale.sale_discount_percent || 0,
      payments: (sale.payments || []).map((payment) => ({
        account_id: payment.account_id,
        account_name: payment.account_name,
        amount: payment.amount || 0,
        payment_method_id: payment.payment_method_id || null,
        payment_method_name: payment.payment_method_name || null,
        display_label: payment.display_label || payment.account_name,
      })),
      items: (sale.items || []).map((item) => ({
        product_id: item.product_id,
        product_name: item.product_name,
        description: item.description || '',
        quantity: item.quantity || 1,
        unit_price: item.unit_price || 0,
        discount: item.discount || 0,
        discount_percent: item.discount_percent || 0,
        vat_regime: item.vat_regime || 'inclusive',
        vat_rate: item.vat_rate || 16,
        item_id: item.item_id || null,
        serial_number: item.serial_number || null,
      })),
    });
    setEditSalePaymentAccount(null);
    setEditSalePaymentAmount('');
    setEditSaleJustification('');
    setEditSaleDialogOpen(true);
  };

  const updateEditSaleLineField = (index, field, value) => {
    setEditSaleForm((current) => ({
      ...current,
      items: (current.items || []).map((item, itemIndex) => (
        itemIndex === index ? { ...item, [field]: value } : item
      )),
    }));
  };

  const updateEditSaleField = (field, value) => {
    setEditSaleForm((current) => ({ ...current, [field]: value }));
  };

  const addEditSalePayment = () => {
    if (!editSalePaymentAccount || !editSalePaymentAmount || parseFloat(editSalePaymentAmount) <= 0) {
      toast.error(t('salesPage.selectAccountAndAmount'));
      return;
    }

    const account = paymentAccounts.find((item) => item.id === editSalePaymentAccount) || dedupedPaymentAccounts.find((item) => item.id === editSalePaymentAccount);
    if (!account) return;

    setEditSaleForm((current) => ({
      ...current,
      payments: [
        ...(current?.payments || []),
        {
          account_id: account.id,
          account_name: account.name,
          payment_method_id: account.payment_method_id,
          payment_method_name: account.payment_method_name,
          display_label: getPaymentDisplayLabel(account),
          amount: parseFloat(editSalePaymentAmount) || 0,
        },
      ],
    }));

    setEditSalePaymentAccount(null);
    setEditSalePaymentAmount('');
  };

  const updateEditSalePaymentAmount = (index, value) => {
    setEditSaleForm((current) => ({
      ...current,
      payments: (current?.payments || []).map((payment, paymentIndex) => (
        paymentIndex === index ? { ...payment, amount: parseFloat(value) || 0 } : payment
      )),
    }));
  };

  const removeEditSalePayment = (index) => {
    setEditSaleForm((current) => ({
      ...current,
      payments: (current?.payments || []).filter((_, paymentIndex) => paymentIndex !== index),
    }));
  };

  const handleSaveEditSale = () => {
    if (!editSaleJustification || editSaleJustification.trim().length < 5) {
      toast.error('Justificação obrigatória (mínimo 5 caracteres)');
      return;
    }

    const editPaymentValidation = getPaymentValidationState(editSaleForm?.payments || [], {
      requireSettlement: false,
      totalOverride: editSaleTotals.total
    });
    if (!editPaymentValidation.isValid) {
      toast.error(editPaymentValidation.message);
      return;
    }

    setConfirmEditDialogOpen(true);
  };

  const handleConfirmEditSale = async () => {
    if (!selectedSale?.id || !editSaleForm) {
      toast.error('Venda inválida para edição');
      return;
    }

    const editPaymentValidation = getPaymentValidationState(editSaleForm.payments || [], {
      requireSettlement: false,
      totalOverride: editSaleTotals.total
    });
    if (!editPaymentValidation.isValid) {
      toast.error(editPaymentValidation.message);
      return;
    }

    try {
      await salesAPI.editSale(selectedSale.id, {
        customer_name: editSaleForm.customer_name || null,
        customer_phone: editSaleForm.customer_phone || null,
        customer_email: editSaleForm.customer_email || null,
        customer_nuit: editSaleForm.customer_nuit || null,
        customer_address: editSaleForm.customer_address || null,
        customer_city: editSaleForm.customer_city || null,
        customer_contact_person: editSaleForm.customer_contact_person || null,
        notes: editSaleForm.notes || null,
        employee_id: editSaleForm.employee_id || null,
        employee_name: employees.find((employee) => employee.id === editSaleForm.employee_id)?.name || editSaleForm.employee_name || null,
        sale_discount: Number(editSaleForm.sale_discount) || 0,
        sale_discount_percent: Number(editSaleForm.sale_discount_percent) || 0,
        payments: (editSaleForm.payments || []).map((payment) => ({
          account_id: payment.account_id,
          account_name: payment.account_name,
          amount: Number(payment.amount) || 0,
          payment_method_id: payment.payment_method_id || null,
          payment_method_name: payment.payment_method_name || null,
        })),
        items: editSaleForm.items,
      }, editSaleJustification.trim());
      
      toast.success(t('salesPage.updateCompleted'));
      setConfirmEditDialogOpen(false);
      setEditSaleDialogOpen(false);
      setEditSaleJustification('');
      loadSales();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao actualizar venda'));
    }
  };

  const handleDirectAdminCancelSale = async (sale) => {
    try {
      const shouldRevertStock = sale.status !== 'draft';
      const result = await salesAPI.cancelSale(
        sale.id,
        'Cancelamento directo pelo administrador',
        'Executado sem diálogo de confirmação (regra admin).',
        shouldRevertStock
      );
      toast.success(result.data.stock_reverted ? t('salesPage.adminCancelledStock') : t('salesPage.adminCancelled'));
      loadSales();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao cancelar venda'));
    }
  };

  const handleOpenCancelSale = (sale) => {
    if (sale.status === 'cancelled') {
      toast.error(t('salesPage.saleAlreadyCancelled'));
      return;
    }
    if (!canEdit) {
      toast.error('Sem permissão para cancelar vendas');
      return;
    }

    if (isAdmin) {
      handleDirectAdminCancelSale(sale);
      return;
    }

    setSelectedSale(sale);
    setCancelSaleReason('');
    setCancelSaleNotes('');
    setRevertStockOnCancel(sale.status !== 'draft');
    setCancelSaleDialogOpen(true);
  };

  const handleConfirmCancelSale = async () => {
    if (!selectedSale?.id) {
      toast.error('Venda inválida para cancelamento');
      return;
    }

    if (!cancelSaleReason || cancelSaleReason.trim().length < 1) {
      toast.error(t('salesPage.cancellationReasonRequired'));
      return;
    }
    
    try {
      const result = await salesAPI.cancelSale(
        selectedSale.id,
        cancelSaleReason.trim(),
        cancelSaleNotes.trim(),
        revertStockOnCancel
      );
      toast.success(result.data.stock_reverted ? t('salesPage.saleCancelledStock') : t('salesPage.saleCancelled'));
      setCancelSaleDialogOpen(false);
      setCancelSaleReason('');
      setCancelSaleNotes('');
      loadSales();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao cancelar venda'));
    }
  };

  // State transitions
  const handleConfirmSale = async (saleId) => {
    try {
      await salesAPI.confirmSale(saleId);
      toast.success(t('salesPage.saleConfirmed'));
      loadSales();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao confirmar venda'));
    }
  };

  const handleMarkPaid = async (saleId) => {
    try {
      await salesAPI.markPaid(saleId);
      toast.success(t('salesPage.saleMarkedPaid'));
      loadSales();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao marcar como paga'));
    }
  };

  const handleCompleteSale = async (saleId) => {
    try {
      await salesAPI.completeSale(saleId);
      toast.success(t('salesPage.saleConcluded'));
      loadSales();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao concluir venda'));
    }
  };

  const handleViewAudit = async (sale) => {
    setSelectedSale(sale);
    try {
      const response = await salesAPI.getSaleAudit(sale.id);
      setAuditLogs(response.data);
      setAuditDialogOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar histórico');
    }
  };

  const toggleSaleSelection = (saleId, checked) => {
    setSelectedSaleIds((prev) => {
      if (checked) {
        return prev.includes(saleId) ? prev : [...prev, saleId];
      }
      return prev.filter((id) => id !== saleId);
    });
  };

  const paginatedSales = useMemo(
    () => paginateItems(sales, salesPage, DEFAULT_TABLE_PAGE_SIZE),
    [sales, salesPage]
  );
  const paginatedCancelledSales = useMemo(
    () => paginateItems(cancelledSales, cancelledSalesPage, DEFAULT_TABLE_PAGE_SIZE),
    [cancelledSales, cancelledSalesPage]
  );

  useEffect(() => {
    setSalesPage((current) => Math.min(current, paginatedSales.totalPages));
  }, [paginatedSales.totalPages]);

  useEffect(() => {
    setCancelledSalesPage((current) => Math.min(current, paginatedCancelledSales.totalPages));
  }, [paginatedCancelledSales.totalPages]);

  const currentSalesCollection = activeSalesTab === 'cancelled' ? paginatedCancelledSales.pageItems : paginatedSales.pageItems;
  const allSalesSelected = currentSalesCollection.length > 0 && currentSalesCollection.every((sale) => selectedSaleIds.includes(sale.id));

  const toggleSelectAllSales = (checked) => {
    if (checked) {
      setSelectedSaleIds(currentSalesCollection.map((sale) => sale.id));
      return;
    }
    setSelectedSaleIds([]);
  };

  const handleBulkSalesStatus = async (action) => {
    if (!selectedSaleIds.length) {
      toast.error(t('salesPage.selectSalesBulkAction'));
      return;
    }
    try {
      const response = await salesAPI.bulkStatus({
        ids: selectedSaleIds,
        action,
        reason: action === 'deactivate' ? 'Cancelamento em massa via lista' : 'Restauro em massa via lista',
      });
      toast.success(response.data.message || 'Ação em massa concluída');
      setSelectedSaleIds([]);
      loadSales();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro na ação em massa'));
    }
  };

  const handleExportSelectedSales = () => {
    if (!selectedSaleIds.length) {
      toast.error(t('salesPage.selectSalesExport'));
      return;
    }
    const selected = sales.filter((sale) => selectedSaleIds.includes(sale.id));
    if (!selected.length) {
      toast.error('Nenhuma venda seleccionada para exportação');
      return;
    }

    const headers = [t('salesPage.saleNumber'), t('labels.date'), t('salesPage.locationLabel'), t('salesPage.employeeShort'), t('labels.customer'), t('labels.amount'), t('salesPage.statusShort')];
    const rows = selected.map((sale) => [
      formatDisplayOnlyCode(sale.sale_number, 'SAL'),
      formatDateTime(sale.created_at),
      sale.location_name || '',
      sale.employee_name || sale.seller_name || '',
      sale.customer_name || 'Anónimo',
      sale.total_amount || 0,
      statusLabels[sale.status] || sale.status,
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(';'))
      .join('\n');

    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = `vendas_seleccionadas_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
    toast.success(`${selected.length} vendas exportadas`);
  };

  const cartMarginPreview = getCartMarginPreview();
  const visiblePartialIssues = useMemo(
    () => partialIssues.filter((issue) => issue.endpoint !== 'settings.active-payment-accounts'),
    [partialIssues]
  );
  const salesTotalAmount = useMemo(
    () => sales.reduce((sum, sale) => sum + (sale.total_amount || 0), 0),
    [sales]
  );
  const activeFilterChips = useMemo(() => {
    const chips = [];
    chips.push({ key: 'period', label: selectedPeriodLabel || t('dashboard.selectedPeriod') });
    if (filters.location_id) {
      const locationName = locations.find((location) => location.id === filters.location_id)?.name;
      if (locationName) chips.push({ key: 'sales-location', label: locationName });
    }
    if (filters.employee_id) {
      const employeeName = employees.find((employee) => employee.id === filters.employee_id)?.name;
      if (employeeName) chips.push({ key: 'employee', label: employeeName });
    }
    if (filters.status) chips.push({ key: 'status', label: statusLabels[filters.status] || filters.status });
    if (filters.payment_account_id) {
      const accountName = paymentAccounts.find((account) => account.id === filters.payment_account_id)?.name;
      if (accountName) chips.push({ key: 'payment-account', label: accountName });
    }
    return chips;
  }, [employees, filters.employee_id, filters.location_id, filters.payment_account_id, filters.status, locations, paymentAccounts, selectedPeriodLabel, t]);
  const hasSalesData = Boolean(sales.length || cancelledSales.length || products.length || paymentAccounts.length || employees.length || locations.length);

  return (
    <ModuleStateBoundary
      loading={loading}
      hasData={hasSalesData}
      partialIssues={visiblePartialIssues}
      loadingTestId="sales-page-loading-state"
      partialIssuesTestId="sales-page-partial-issues"
      emptyState={
        <div className="p-6 space-y-6" data-testid="sales-page">
          <EmptyStatePanel
            title={t('salesPage.emptyTitle')}
            description={t('salesPage.emptyDescription')}
            actionLabel={t('gamingCenter.reload')}
            onAction={loadData}
            testId="sales-page-empty-state"
          />
        </div>
      }
    >
    <div className="space-y-4 p-4 lg:p-5" data-testid="sales-page">
      <ModulePageHero
        testId="sales-page-hero"
        title={t('salesPage.title')}
        description={t('salesPage.description')}
        icon={ShoppingCart}
        badge="POS"
        compact
        actions={(
          <div className="flex flex-wrap justify-end gap-2 lg:flex-nowrap">
            <Button
              variant="outline"
              disabled={!selectedSaleIds.length}
              onClick={() => handleBulkSalesStatus('deactivate')}
              data-testid="sales-bulk-deactivate-button"
              size="sm"
              className="h-9 rounded-xl px-3 text-sm"
            >
              {t('salesPage.cancelSelected', { count: selectedSaleIds.length })}
            </Button>
            <Button
              variant="outline"
              disabled={!selectedSaleIds.length}
              onClick={() => handleBulkSalesStatus('restore_soft')}
              data-testid="sales-bulk-restore-button"
              size="sm"
              className="h-9 rounded-xl px-3 text-sm"
            >
              {t('salesPage.restoreDraft')}
            </Button>
            <Button
              variant="outline"
              disabled={!selectedSaleIds.length}
              onClick={handleExportSelectedSales}
              data-testid="sales-bulk-export-button"
              size="sm"
              className="h-9 rounded-xl px-3 text-sm"
            >
              {t('salesPage.export')}
            </Button>
            <Button onClick={openNewSale} data-testid="new-sale-button" size="sm" className="h-9 rounded-xl px-4 text-sm">
              <Plus className="mr-2 h-4 w-4" />
              {t('salesPage.newSale')}
            </Button>
          </div>
        )}
        footer={(
          <div className="grid gap-3 xl:grid-cols-4" data-testid="sales-hero-metrics-strip">
            {[
              { key: 'count', label: t('salesPage.recentSales'), value: sales.length, helper: t('salesPage.filteredPeriodRecords') },
              { key: 'total', label: t('salesPage.totalSold'), value: formatCurrency(salesTotalAmount), helper: t('salesPage.aggregatedAmount') },
              { key: 'cancelled', label: t('salesPage.cancelled'), value: cancelledSales.length, helper: t('salesPage.cancelledHelper') },
              { key: 'cart', label: t('salesPage.cart'), value: cart.length, helper: t('salesPage.cartHelper') },
            ].map((metric) => (
              <div
                key={metric.key}
                className="rounded-2xl border border-slate-200 bg-white/90 px-4 py-3 shadow-none"
                data-testid={`sales-hero-metric-${metric.key}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-[0.16em] text-slate-500">{metric.label}</p>
                    {metric.helper ? <p className="mt-1 text-xs text-slate-500">{metric.helper}</p> : null}
                  </div>
                  <p className="shrink-0 text-right text-xl font-semibold text-slate-900">{metric.value}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      />

      <SalesFiltersDeck
        salesCount={sales.length}
        totalSoldLabel={formatCurrency(salesTotalAmount)}
        activeLocationName={selectedLocation?.name}
        activeFilterChips={activeFilterChips}
        period={filters.period || 'all'}
        onPeriodChange={(value) => setFilters({ period: value, custom_start: value === 'custom' ? (filters.custom_start || '') : null, custom_end: value === 'custom' ? (filters.custom_end || '') : null })}
        periodOptions={periodOptions}
        customStartDate={filters.custom_start || ''}
        customEndDate={filters.custom_end || ''}
        onCustomStartChange={(value) => setFilters({ custom_start: value || null })}
        onCustomEndChange={(value) => setFilters({ custom_end: value || null })}
        locationValue={filters.location_id || 'all'}
        onLocationChange={handleSalesFilterLocationChange}
        locations={locations}
        employeeValue={filters.employee_id || 'all'}
        onEmployeeChange={(value) => setFilters({ employee_id: value })}
        employees={employees}
        statusValue={filters.status || 'all'}
        onStatusChange={(value) => setFilters({ status: value })}
        statusOptions={SALES_STATUS_OPTIONS}
        statusLabels={statusLabels}
        paymentAccountValue={filters.payment_account_id || 'all'}
        onPaymentAccountChange={(value) => setFilters({ payment_account_id: value })}
        paymentAccounts={paymentAccounts}
        onResetFilters={() => setFilters({ period: 'all', location_id: null, employee_id: null, status: null, payment_account_id: null, custom_start: null, custom_end: null })}
        actionsSlot={(
          <ExportButton 
            module="sales" 
            label="Exp."
            filters={{
              period: filters.period,
              location_id: filters.location_id,
              employee_id: filters.employee_id,
              status: filters.status,
              payment_account_id: filters.payment_account_id,
              start_date: filters.period === 'custom' ? buildMaputoRangeBoundary(filters.custom_start, 'start') : undefined,
              end_date: filters.period === 'custom' ? buildMaputoRangeBoundary(filters.custom_end, 'end') : undefined,
            }}
          />
        )}
      />

      <Tabs value={activeSalesTab} onValueChange={handleSalesTabChange} className="space-y-3" data-testid="sales-tabs-root">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1.5 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
          <TabsTrigger value="sales" className="h-9 rounded-full border border-slate-200 bg-white px-3.5 text-xs shadow-none data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="sales-tab-trigger">{t('salesPage.salesTab')}</TabsTrigger>
          <TabsTrigger value="cash" className="h-9 rounded-full border border-slate-200 bg-white px-3.5 text-xs shadow-none data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="cash-integration-tab-trigger">{t('salesPage.cashTab')}</TabsTrigger>
          {canEdit && (
            <TabsTrigger value="cancelled" className="h-9 rounded-full border border-slate-200 bg-white px-3.5 text-xs shadow-none data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="cancelled-sales-tab-trigger">
              {t('salesPage.cancelledTab')}
            </TabsTrigger>
          )}
        </TabsList>

        {/* Sales Tab */}
        <TabsContent value="sales" className="space-y-3">
          <Card className="border-slate-200 shadow-sm" data-testid="sales-table">
            <CardHeader className="px-4 py-3.5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                  <CardTitle className="flex items-center gap-2.5 text-xl font-semibold">
                  <ShoppingCart className="h-4 w-4" />
                  {t('salesPage.recentSalesTitle', { count: sales.length })}
                </CardTitle>
                {selectedSaleIds.length > 0 && activeSalesTab === 'sales' && (
                  <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700" data-testid="sales-selection-badge">
                    {selectedSaleIds.length} seleccionad{selectedSaleIds.length > 1 ? 'os' : 'o'}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {loading ? (
                <p className="text-center text-muted-foreground py-4">{t('salesPage.loadingSales')}</p>
              ) : sales.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">{t('salesPage.noSales')}</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table className="text-sm leading-relaxed" data-testid="sales-list-table">
                    <TableHeader className="sticky top-0 z-10 bg-white">
                      <TableRow className="border-b border-slate-200 bg-slate-50/95 hover:bg-slate-50/95">
                        <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'sticky left-0 z-20 bg-slate-50/95')} style={SALES_SELECTOR_COLUMN_STYLE}>
                          <Checkbox
                            checked={allSalesSelected}
                            onCheckedChange={(checked) => toggleSelectAllSales(Boolean(checked))}
                            data-testid="sales-select-all-checkbox"
                          />
                        </TableHead>
                        <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative')} style={getSalesColumnStyle('sale')}>Venda<ResizableTableHandle onMouseDown={(event) => startSalesColumnResize('sale', event)} testId="sales-column-resize-sale" /></TableHead>
                        <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative')} style={getSalesColumnStyle('operation')}>Operação<ResizableTableHandle onMouseDown={(event) => startSalesColumnResize('operation', event)} testId="sales-column-resize-operation" /></TableHead>
                        <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative')} style={getSalesColumnStyle('sku')}>SKU<ResizableTableHandle onMouseDown={(event) => startSalesColumnResize('sku', event)} testId="sales-column-resize-sku" /></TableHead>
                        <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative', 'text-right')} style={getSalesColumnStyle('quantity')}>QTD<ResizableTableHandle onMouseDown={(event) => startSalesColumnResize('quantity', event)} testId="sales-column-resize-quantity" /></TableHead>
                        <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative', 'text-right')} style={getSalesColumnStyle('total')}>Total<ResizableTableHandle onMouseDown={(event) => startSalesColumnResize('total', event)} testId="sales-column-resize-total" /></TableHead>
                        <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative', 'text-center')} style={getSalesColumnStyle('status')}>Estado<ResizableTableHandle onMouseDown={(event) => startSalesColumnResize('status', event)} testId="sales-column-resize-status" /></TableHead>
                        <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative', 'text-center')} style={getSalesColumnStyle('actions')}>Acções<ResizableTableHandle onMouseDown={(event) => startSalesColumnResize('actions', event)} testId="sales-column-resize-actions" /></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedSales.pageItems.map((sale) => {
                        const isSelected = selectedSaleIds.includes(sale.id);
                        const quantityTotal = getSaleQuantityTotal(sale);
                        const skuSummary = getSaleSkuSummary(sale);
                        return (
                          <TableRow key={sale.id} className={cn(INVENTORY_TABLE_ROW_CLASS, isSelected && 'bg-indigo-50/35')} data-testid={`sale-row-${sale.id}`}>
                            <TableCell className={cn(INVENTORY_TABLE_CELL_CLASS, 'sticky left-0 z-10 bg-white group-hover:bg-indigo-50/35')} style={SALES_SELECTOR_COLUMN_STYLE}>
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={(checked) => toggleSaleSelection(sale.id, Boolean(checked))}
                                data-testid={`sales-select-checkbox-${sale.id}`}
                              />
                            </TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS} style={getSalesColumnStyle('sale')}>
                              <div className="space-y-1">
                                <p className="font-mono text-sm font-semibold text-slate-900">{formatDisplayOnlyCode(sale.sale_number, 'SAL')}</p>
                                <p className="text-xs text-slate-500">{formatDateTime(sale.created_at)}</p>
                              </div>
                            </TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS} style={getSalesColumnStyle('operation')}>
                              <div className="space-y-1">
                                <p className="text-sm font-medium text-slate-900">{sale.customer_name || 'Anónimo'}</p>
                                <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                                  <span data-testid={`sale-location-inline-${sale.id}`}>{sale.location_name || 'Sem local'}</span>
                                  <span>•</span>
                                  <span data-testid={`sale-operator-inline-${sale.id}`}>{sale.employee_name || sale.seller_name || 'Sem operador'}</span>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS} data-testid={`sale-sku-summary-${sale.id}`} style={getSalesColumnStyle('sku')}>
                              <div className="space-y-1">
                                <p className="font-mono text-xs font-semibold text-slate-900">{skuSummary}</p>
                                <p className="text-xs text-slate-500">{sale.item_count || (sale.items || []).length || 0} linha(s)</p>
                              </div>
                            </TableCell>
                            <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} text-right`} data-testid={`sale-quantity-total-${sale.id}`} style={getSalesColumnStyle('quantity')}>
                              <span className="text-sm font-semibold text-slate-900">{quantityTotal || '—'}</span>
                            </TableCell>
                            <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} text-right font-medium text-green-600`} style={getSalesColumnStyle('total')}>
                              <div className="space-y-1">
                                <p data-testid={`sale-total-amount-${sale.id}`}>{formatCurrency(sale.total_amount)}</p>
                                <p className="text-xs font-normal text-slate-500" data-testid={`sale-payment-method-summary-${sale.id}`}>{summarizeSalePaymentMethods(sale)}</p>
                              </div>
                            </TableCell>
                            <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} text-center`} style={getSalesColumnStyle('status')}>
                              <Badge className={statusColors[sale.status]} variant="outline">
                                {statusLabels[sale.status] || sale.status}
                              </Badge>
                            </TableCell>
                            <TableCell className={INVENTORY_TABLE_CELL_CLASS} style={getSalesColumnStyle('actions')}>
                              <UniversalCrudActions
                                entityLabel="sale"
                                entityId={sale.id}
                                canView
                                canEdit={sale.status !== 'cancelled' && canManageSaleEdits}
                                canDelete={sale.status !== 'cancelled' && canEdit}
                                canRecover={false}
                                align="center"
                                compact
                                onView={() => handleViewSale(sale)}
                                onEdit={() => handleOpenEditSale(sale)}
                                onDelete={() => handleOpenCancelSale(sale)}
                                extraActions={
                                  <div className="flex flex-nowrap items-center gap-1">
                                    {sale.status === 'draft' && (
                                      <Button size="icon" variant="outline" className="h-8 w-8 rounded-lg border-blue-200 text-blue-700 hover:bg-blue-50" onClick={() => handleConfirmSale(sale.id)} title="Confirmar" data-testid={`sale-confirm-button-${sale.id}`}>
                                        <Check className="h-4 w-4" />
                                        <span className="sr-only">Confirmar</span>
                                      </Button>
                                    )}
                                    {sale.status === 'confirmed' && (
                                      <Button size="icon" variant="outline" className="h-8 w-8 rounded-lg border-green-200 text-green-700 hover:bg-green-50" onClick={() => handleMarkPaid(sale.id)} title="Marcar paga" data-testid={`sale-mark-paid-button-${sale.id}`}>
                                        <DollarSign className="h-4 w-4" />
                                        <span className="sr-only">Marcar paga</span>
                                      </Button>
                                    )}
                                    {sale.status === 'paid' && (
                                      <Button size="icon" variant="outline" className="h-8 w-8 rounded-lg border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => handleCompleteSale(sale.id)} title="Concluir" data-testid={`sale-complete-button-${sale.id}`}>
                                        <CheckCircle className="h-4 w-4" />
                                        <span className="sr-only">Concluir</span>
                                      </Button>
                                    )}
                                    {canEdit && (
                                      <Button size="icon" variant="ghost" className="h-8 w-8 rounded-lg" onClick={() => handleViewAudit(sale)} title="Ver histórico" data-testid={`sale-audit-button-${sale.id}`}>
                                        <History className="h-4 w-4 text-indigo-600" />
                                        <span className="sr-only">Ver histórico</span>
                                      </Button>
                                    )}
                                  </div>
                                }
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <TablePaginationBar
                    page={paginatedSales.safePage}
                    totalItems={sales.length}
                    pageSize={DEFAULT_TABLE_PAGE_SIZE}
                    onPageChange={setSalesPage}
                    testIdPrefix="sales-list-table"
                    itemLabel="vendas"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cash" className="space-y-4" data-testid="sales-cash-tab-content">
          <Card className="border-slate-200 shadow-sm" data-testid="sales-cash-location-card">
            <CardContent className="p-4">
              <div className="grid gap-2 sm:max-w-md">
                <Label htmlFor="sales-cash-location-select">{t('salesPage.activeLocation')}</Label>
                <Select
                  value={selectedLocation?.id || undefined}
                  onValueChange={handleLocationChange}
                >
                  <SelectTrigger id="sales-cash-location-select" className="h-10 rounded-xl" data-testid="sales-cash-location-select">
                    <SelectValue placeholder={t('salesPage.selectLocation')} />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <SalesCashIntegrationPanel
            selectedLocation={selectedLocation}
            activeSession={activeCashSession}
            loadingSession={loadingCashSession}
            canManageCash={canManageCashSession}
            onRefreshSession={loadActiveCashSession}
            autoOpenSessionRequested={activeSalesTab === 'cash' && searchParams.get('open_session') === '1'}
            onAutoOpenSessionHandled={handleClearOpenCashRequest}
          />
        </TabsContent>

        {/* Cancelled Sales Tab */}
        {canEdit && (
          <TabsContent value="cancelled" className="space-y-4" data-testid="cancelled-sales-tab-content">
            <Card data-testid="cancelled-sales-card">
              <CardHeader>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <CardTitle className="flex items-center gap-2 text-red-600">
                    <XCircle className="h-5 w-5" />
                    {t('salesPage.cancelledSalesTitle', { count: cancelledSales.length })}
                  </CardTitle>
                  <div className="flex flex-wrap items-center gap-3">
                    {selectedSaleIds.length > 0 && activeSalesTab === 'cancelled' && (
                      <Badge variant="outline" className="rounded-full border-red-200 bg-red-50 text-red-700" data-testid="cancelled-sales-selection-badge">
                        {selectedSaleIds.length} seleccionad{selectedSaleIds.length > 1 ? 'os' : 'o'}
                      </Badge>
                    )}
                    {isAdmin && cancelledSales.length > 0 && (
                      <Button
                        variant="destructive"
                        onClick={() => setPurgeCancelledDialog({ open: true, sale: null })}
                        data-testid="cancelled-sales-purge-all-button"
                      >
                        {t('salesPage.deleteCancelled')}
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {cancelledSales.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4" data-testid="cancelled-sales-empty-state">{t('salesPage.noCancelledSales')}</p>
                ) : (
                  <div className="overflow-x-auto" data-testid="cancelled-sales-table-wrapper">
                    <Table className="text-sm" data-testid="cancelled-sales-table">
                      <TableHeader className="sticky top-0 z-10 bg-white">
                        <TableRow className="border-b border-red-200 bg-red-50/90 hover:bg-red-50/90">
                          <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative')} style={getCancelledSalesColumnStyle('selector')}>
                            <Checkbox
                              checked={allSalesSelected}
                              onCheckedChange={(checked) => toggleSelectAllSales(Boolean(checked))}
                              data-testid="cancelled-sales-select-all-checkbox"
                            />
                            <ResizableTableHandle onMouseDown={(event) => startCancelledSalesColumnResize('selector', event)} testId="cancelled-sales-column-resize-selector" />
                          </TableHead>
                          <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative')} style={getCancelledSalesColumnStyle('document')}>{t('salesPage.cancelledDocument')}<ResizableTableHandle onMouseDown={(event) => startCancelledSalesColumnResize('document', event)} testId="cancelled-sales-column-resize-document" /></TableHead>
                          <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative')} style={getCancelledSalesColumnStyle('datetime')}>{t('salesPage.cancelledDatetime')}<ResizableTableHandle onMouseDown={(event) => startCancelledSalesColumnResize('datetime', event)} testId="cancelled-sales-column-resize-datetime" /></TableHead>
                          <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative')} style={getCancelledSalesColumnStyle('customer')}>{t('labels.customer')}<ResizableTableHandle onMouseDown={(event) => startCancelledSalesColumnResize('customer', event)} testId="cancelled-sales-column-resize-customer" /></TableHead>
                          <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative')} style={getCancelledSalesColumnStyle('cancelled_by')}>{t('salesPage.cancelledBy')}<ResizableTableHandle onMouseDown={(event) => startCancelledSalesColumnResize('cancelled_by', event)} testId="cancelled-sales-column-resize-cancelled-by" /></TableHead>
                          <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative', 'text-right')} style={getCancelledSalesColumnStyle('amount')}>{t('labels.amount')}<ResizableTableHandle onMouseDown={(event) => startCancelledSalesColumnResize('amount', event)} testId="cancelled-sales-column-resize-amount" /></TableHead>
                          <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative')} style={getCancelledSalesColumnStyle('reason')}>{t('salesPage.cancelledReason')}<ResizableTableHandle onMouseDown={(event) => startCancelledSalesColumnResize('reason', event)} testId="cancelled-sales-column-resize-reason" /></TableHead>
                          <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative', 'text-center')} style={getCancelledSalesColumnStyle('stock')}>{t('salesPage.cancelledStock')}<ResizableTableHandle onMouseDown={(event) => startCancelledSalesColumnResize('stock', event)} testId="cancelled-sales-column-resize-stock" /></TableHead>
                          <TableHead className={cn(INVENTORY_TABLE_HEAD_CLASS, 'relative', 'text-right')} style={getCancelledSalesColumnStyle('actions')}>{t('salesPage.actions')}<ResizableTableHandle onMouseDown={(event) => startCancelledSalesColumnResize('actions', event)} testId="cancelled-sales-column-resize-actions" /></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedCancelledSales.pageItems.map((sale) => {
                          const isSelected = selectedSaleIds.includes(sale.id);
                          return (
                            <TableRow key={sale.id} className={cn('group border-b border-red-100 bg-red-50/65 transition-all hover:bg-red-100/70', isSelected && 'bg-red-100/80')} data-testid={`cancelled-sale-row-${sale.id}`}>
                              <TableCell className={INVENTORY_TABLE_CELL_CLASS} style={getCancelledSalesColumnStyle('selector')}>
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={(checked) => toggleSaleSelection(sale.id, Boolean(checked))}
                                  data-testid={`cancelled-sales-select-checkbox-${sale.id}`}
                                />
                              </TableCell>
                              <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} font-mono text-sm`} data-testid={`cancelled-sale-doc-number-${sale.id}`} style={getCancelledSalesColumnStyle('document')}>
                                {formatDisplayOnlyCode(sale.fiscal_document_number || sale.sale_number, 'FAC')}
                              </TableCell>
                              <TableCell className={INVENTORY_TABLE_CELL_CLASS} data-testid={`cancelled-sale-datetime-${sale.id}`} style={getCancelledSalesColumnStyle('datetime')}>{formatDateTime(sale.cancelled_at)}</TableCell>
                              <TableCell className={INVENTORY_TABLE_CELL_CLASS} data-testid={`cancelled-sale-customer-${sale.id}`} style={getCancelledSalesColumnStyle('customer')}>{sale.customer_name || 'N/A'}</TableCell>
                              <TableCell className={INVENTORY_TABLE_CELL_CLASS} data-testid={`cancelled-sale-by-${sale.id}`} style={getCancelledSalesColumnStyle('cancelled_by')}>
                                {sale.cancelled_by_user_name || sale.cancelled_by_name || 'N/A'}
                              </TableCell>
                              <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} text-right font-medium`} data-testid={`cancelled-sale-total-${sale.id}`} style={getCancelledSalesColumnStyle('amount')}>{formatCurrency(sale.total_amount)}</TableCell>
                              <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} text-sm text-red-600`} data-testid={`cancelled-sale-reason-${sale.id}`} style={getCancelledSalesColumnStyle('reason')}>
                                {sale.cancel_reason}
                              </TableCell>
                              <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} text-center`} data-testid={`cancelled-sale-stock-status-${sale.id}`} style={getCancelledSalesColumnStyle('stock')}>
                                <Badge variant={sale.stock_reverted ? "default" : "secondary"}>
                                  {sale.stock_reverted ? t('salesPage.stockReturned') : t('salesPage.stockNotReturned')}
                                </Badge>
                              </TableCell>
                              <TableCell className={`${INVENTORY_TABLE_CELL_CLASS} text-right`} style={getCancelledSalesColumnStyle('actions')}>
                                <div className="flex justify-end gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleViewSale(sale)}
                                    data-testid={`cancelled-sale-details-btn-${sale.id}`}
                                  >
                                    {t('salesPage.details')}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handlePrintCancelledSale(sale)}
                                    data-testid={`cancelled-sale-print-btn-${sale.id}`}
                                  >
                                    {t('salesPage.printCancelled')}
                                  </Button>
                                  {isAdmin && (
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => setPurgeCancelledDialog({ open: true, sale })}
                                      data-testid={`cancelled-sale-hard-delete-btn-${sale.id}`}
                                    >
                                      {t('salesPage.delete')}
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    <TablePaginationBar
                      page={paginatedCancelledSales.safePage}
                      totalItems={cancelledSales.length}
                      pageSize={DEFAULT_TABLE_PAGE_SIZE}
                      onPageChange={setCancelledSalesPage}
                      testIdPrefix="cancelled-sales-table"
                      itemLabel="vendas canceladas"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* POS Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[min(96vw,1280px)] max-w-[1280px] max-h-[88vh] overflow-hidden flex flex-col p-4 sm:p-5">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              {t('salesPage.posDialogTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('salesPage.posDialogDescription')}
            </DialogDescription>
          </DialogHeader>
          
          {/* Location Selector - P1.1 */}
          <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg border mb-2">
            <div className="flex items-center gap-2">
              <Store className="h-5 w-5 text-primary" />
              <span className="font-medium">{t('salesPage.locationLabel')}</span>
            </div>
            <Select
              value={selectedLocation?.id}
              onValueChange={handleLocationChange}
            >
              <SelectTrigger className="w-[250px]" data-testid="location-select">
                <SelectValue placeholder={t('salesPage.selectStoreLocation')} />
              </SelectTrigger>
              <SelectContent>
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      {loc.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedLocation && (
              <Badge variant="outline" className="text-primary">
                {t('salesPage.sellingAt', { location: selectedLocation.name })}
              </Badge>
            )}
            
            {/* Scanner Indicator - P7 */}
            <div className="ml-auto">
              <ScannerIndicator scanning={scanning} lastScan={lastScan} enabled={!!selectedLocation} />
            </div>
          </div>
          
          <div className="grid grid-cols-5 gap-4 flex-1 overflow-hidden">
            {/* Products & Cart */}
            <div className="col-span-3 flex flex-col space-y-3 overflow-hidden">
              {/* Product Search */}
              <Popover open={productSearchOpen} onOpenChange={setProductSearchOpen}>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    role="combobox" 
                    className="w-full justify-between h-12 text-left"
                    disabled={!selectedLocation}
                    data-testid="product-search-trigger"
                  >
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <Search className="h-4 w-4" />
                      {selectedLocation ? t('salesPage.searchProduct') : t('salesPage.selectLocationFirst')}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[500px] p-0" align="start">
                  <Command>
                    <CommandInput 
                      placeholder={t('salesPage.searchProductPlaceholder')} 
                      value={productSearchQuery}
                      onValueChange={setProductSearchQuery}
                      data-testid="product-search-input"
                    />
                    <CommandList>
                      <CommandEmpty>
                        {productAvailabilityLoading ? t('salesPage.loadingProducts') : t('salesPage.noProductsFound')}
                      </CommandEmpty>
                      <CommandGroup heading={t('salesPage.availableProducts')}>
                        {productAvailabilityLoading && (
                          <div className="px-3 py-2 text-xs text-muted-foreground" data-testid="pos-products-loading-state">
                            {t('salesPage.loadingProducts')}
                          </div>
                        )}
                        {filteredProducts.slice(0, 10).map((product) => {
                          const selectedLocationAvailable = getSelectedLocationAvailable(product);
                          const isUnavailableInSelectedLocation = selectedLocationAvailable <= 0;

                          return (
                            <CommandItem
                              key={product.id}
                              value={`${product.name} ${product.sku}`}
                              onSelect={() => handleProductSelect(product)}
                              disabled={isUnavailableInSelectedLocation}
                              className={cn(
                                'flex items-center justify-between cursor-pointer',
                                isUnavailableInSelectedLocation && 'opacity-60'
                              )}
                              data-testid={`pos-product-option-${product.id}`}
                            >
                              <div className="flex items-center gap-2">
                                {product.is_trackable ? (
                                  <Smartphone className="h-4 w-4 text-indigo-500" />
                                ) : (
                                  <Package className="h-4 w-4 text-slate-500" />
                                )}
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium">{product.name}</p>
                                    {product.is_kit && (
                                      <Badge variant="secondary" className="text-[10px]" data-testid={`pos-kit-badge-${product.id}`}>
                                        KIT
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    {product.sku} | Stock total: {product.total_available ?? product.current_stock}
                                  </p>
                                  {product.pos_description && (
                                    <div className="grid grid-cols-[72px_minmax(0,1fr)] gap-1 text-[11px] text-slate-700" data-testid={`pos-product-description-${product.id}`}>
                                      <span className="font-semibold text-slate-500">Descrição</span>
                                      <span>{product.pos_description}</span>
                                    </div>
                                  )}
                                  {product.is_kit && (
                                    <p className="text-[11px] text-emerald-700" data-testid={`pos-kit-components-note-${product.id}`}>
                                      {t('salesPage.includesComponents', { count: product.kit_component_count || 0 })}
                                    </p>
                                  )}
                                  <p className="text-xs" data-testid={`pos-product-availability-${product.id}`}>
                                    <span className="text-muted-foreground">{t('salesPage.availableItemsAt')} </span>
                                    {(product.availability_by_location || []).map((loc, idx) => (
                                      <span
                                        key={`${product.id}-${loc.location_id}`}
                                        className={cn(
                                          loc.is_current ? 'font-semibold text-indigo-700' : 'text-muted-foreground'
                                        )}
                                        data-testid={`pos-product-location-${product.id}-${loc.location_id}`}
                                      >
                                        {idx > 0 && <span className="text-muted-foreground"> • </span>}
                                        {loc.location_name} ({loc.available})
                                      </span>
                                    ))}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <span className="font-medium" data-testid={`pos-product-price-${product.id}`}>{formatCurrency(product.unit_price)}</span>
                                {isUnavailableInSelectedLocation && (
                                  <p className="text-[11px] text-amber-700" data-testid={`pos-product-selected-location-warning-${product.id}`}>
                                    {t('salesPage.noStockAtLocation', { location: selectedLocation?.name || '' })}
                                  </p>
                                )}
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {/* Cart Items */}
              <div className="flex-1 border rounded-lg overflow-y-auto">
                {cart.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-8">
                    <ShoppingCart className="h-10 w-10 mb-2 opacity-50" />
                    <p>{t('salesPage.emptyCart')}</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {cart.map((item, index) => {
                      const lineMetrics = cartMarginPreview.lines[index] || {};
                      const lineConfidence = lineMetrics.costInfo?.confidence || 'missing';
                      return (
                      <div key={item.cart_line_id || item.item_id || item.id || item.product_id || `${item.name || 'cart-item'}-${index}`} className="p-3 space-y-2 hover:bg-slate-50">
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              {item.is_trackable ? (
                                <Smartphone className="h-4 w-4 text-indigo-500" />
                              ) : (
                                <Package className="h-4 w-4 text-slate-500" />
                              )}
                              <p className="font-medium">{item.product_name}</p>
                              {item.is_kit && (
                                <Badge variant="secondary" className="text-[10px]" data-testid={`cart-kit-badge-${index}`}>
                                  {item.kit_code || 'KIT'}
                                </Badge>
                              )}
                            </div>
                            {item.is_trackable && (
                              <p className="text-xs font-mono text-indigo-600">{item.serial_number}</p>
                            )}
                            {item.description && item.description !== item.product_name && (
                              <div className="grid grid-cols-[72px_minmax(0,1fr)] gap-1 text-[11px] text-slate-700" data-testid={`cart-line-description-${index}`}>
                                <span className="font-semibold text-slate-500">Descrição</span>
                                <span>{item.description}</span>
                              </div>
                            )}
                            {item.is_kit && (
                              <p className="text-xs text-emerald-700" data-testid={`cart-kit-components-${index}`}>
                                {t('salesPage.includesComponents', { count: item.kit_component_count || 0 })}
                              </p>
                            )}
                            <div className="mt-1 space-y-0.5" data-testid={`cart-line-cost-metrics-${index}`}>
                              <p className="text-[11px] text-slate-600" data-testid={`cart-line-cost-${index}`}>
                                Custo usado: {lineConfidence === 'missing' ? 'N/A' : formatCurrency(lineMetrics.lineCost || 0)}
                              </p>
                              <p className="text-[11px] text-slate-600" data-testid={`cart-line-profit-${index}`}>
                                Lucro: {lineMetrics.lineProfit == null ? 'N/A' : formatCurrency(lineMetrics.lineProfit || 0)}
                              </p>
                              <p className="text-[11px] text-slate-600" data-testid={`cart-line-margin-${index}`}>
                                Margem: {lineMetrics.lineMarginPct == null ? 'N/A' : `${Number(lineMetrics.lineMarginPct || 0).toFixed(2)}%`}
                              </p>
                              {lineConfidence !== 'ok' && (
                                <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px]" data-testid={`cart-line-margin-not-reliable-${index}`}>
                                  {t('salesPage.unreliableMargin')}
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {!item.is_trackable ? (
                              <div className="flex items-center border rounded">
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => updateQuantity(index, item.quantity - 1)}>-</Button>
                                <span className="w-6 text-center text-sm">{item.quantity}</span>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => updateQuantity(index, item.quantity + 1)}>+</Button>
                              </div>
                            ) : (
                              <Badge variant="outline">1 un.</Badge>
                            )}
                            
                            <span className="w-20 text-right font-medium text-sm">{formatCurrency(item.quantity * item.unit_price)}</span>
                            
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => removeFromCart(index)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Item Discount */}
                        <div className="flex items-center gap-2 text-xs pl-6">
                          <Percent className="h-3 w-3 text-green-600" />
                          <Input
                            type="number"
                            min="0"
                            placeholder="Desconto MT"
                            className="h-6 w-20 text-xs"
                            value={item.discount || ''}
                            onChange={(e) => updateItemDiscount(index, e.target.value, false)}
                          />
                          <span className="text-muted-foreground">ou</span>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            placeholder="%"
                            className="h-6 w-14 text-xs"
                            value={item.discount_percent || ''}
                            onChange={(e) => updateItemDiscount(index, e.target.value, true)}
                          />
                          <span className="text-muted-foreground">%</span>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Summary Panel */}
            <div className="col-span-2 flex flex-col space-y-3 overflow-y-auto">
              {/* Employee Selection */}
              <div className="p-3 border rounded-lg bg-indigo-50">
                <Label className="text-sm font-medium text-indigo-800">{t('salesPage.employeeForSale')}</Label>
                <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                  <SelectTrigger className="mt-1" data-testid="employee-select-trigger">
                    <SelectValue placeholder={t('salesPage.selectEmployee')} />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id} data-testid={`employee-option-${emp.id}`}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Cliente Selector */}
              <div className="p-3 border rounded-lg space-y-2" data-testid="client-selector-card">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium">{t('salesPage.clientLabel')}</h3>
                  {selectedClient && (
                    <Badge variant="outline" className="text-xs" data-testid="selected-client-code">
                      {selectedClient.client_code || selectedClient.code || 'SEM CÓDIGO'}
                    </Badge>
                  )}
                </div>
                <Popover open={clientSearchOpen} onOpenChange={setClientSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between h-10 text-left"
                      data-testid="client-search-trigger"
                    >
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <User className="h-4 w-4" />
                        {selectedClient ? selectedClient.name : t('salesPage.searchClient')}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[450px] p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder={t('salesPage.clientSearchPlaceholder')}
                        value={clientSearchQuery}
                        onValueChange={setClientSearchQuery}
                        data-testid="client-search-input"
                      />
                      <CommandList>
                        <CommandEmpty>
                          <div className="space-y-2 p-4 text-sm">
                            <p>{t('salesPage.noClientFound')}</p>
                            <div className="flex flex-wrap gap-2">
                              <Button size="sm" onClick={() => handleOpenCreateClient()} data-testid="client-create-button">
                                {t('salesPage.createClient')}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleSelectDefaultClient()} data-testid="client-select-indifferent-button">
                                {t('salesPage.indifferentClient')}
                              </Button>
                            </div>
                          </div>
                        </CommandEmpty>
                        <CommandGroup heading={t('salesPage.foundClients')}>
                          {clientResults.map((client) => (
                            <CommandItem
                              key={client.id}
                              value={`${client.name} ${client.code || ''}`}
                              onSelect={() => handleSelectClient(client)}
                              className="flex items-center justify-between cursor-pointer"
                              data-testid={`client-option-${client.id}`}
                            >
                              <div>
                                <p className="font-medium">{client.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {client.client_code || client.code || 'SEM CÓDIGO'} | {client.nuit || 'Sem NUIT'}
                                </p>
                              </div>
                              <Badge variant="secondary" className="text-xs">
                                {client.client_type === 'company' ? t('salesPage.company') : t('salesPage.individual')}
                              </Badge>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                        {defaultClient && (
                          <CommandGroup heading={t('salesPage.quickClient')}>
                            <CommandItem
                              value={defaultClient.name}
                              onSelect={() => handleSelectDefaultClient()}
                              className="flex items-center justify-between cursor-pointer"
                              data-testid="client-default-option"
                            >
                              <div>
                                <p className="font-medium">{defaultClient.name}</p>
                                <p className="text-xs text-muted-foreground">{defaultClient.client_code || defaultClient.code}</p>
                              </div>
                              <Badge variant="outline" className="text-xs">Default</Badge>
                            </CommandItem>
                          </CommandGroup>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>

                {selectedClient && (
                  <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground" data-testid="selected-client-details">
                    <div>NUIT: {selectedClient.nuit || 'N/A'}</div>
                    <div>Contacto: {selectedClient.phone || 'N/A'}</div>
                    <div>Email: {selectedClient.email || 'N/A'}</div>
                    <div>Morada: {selectedClient.address || 'N/A'}</div>
                  </div>
                )}
              </div>

              {/* Sale Discount */}
              <div className="p-3 border rounded-lg space-y-2">
                <h3 className="text-sm font-medium flex items-center gap-1">
                  <Percent className="h-4 w-4 text-green-600" />
                  {t('salesPage.saleDiscount')}
                </h3>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min="0"
                    placeholder={t('salesPage.amountMt')}
                    className="h-8 text-sm"
                    value={saleDiscountValue || ''}
                    onChange={(e) => setSaleDiscountValue(parseFloat(e.target.value) || 0)}
                  />
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="%"
                    className="h-8 w-20 text-sm"
                    value={saleDiscountPercent || ''}
                    onChange={(e) => setSaleDiscountPercent(parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>

              {/* Payment Methods */}
              <div className="p-3 border rounded-lg space-y-2 flex-1">
                <h3 className="text-sm font-medium" data-testid="pos-payment-form-label">{t('salesPage.paymentMethod')}</h3>
                
                {/* Added payments */}
                {payments.length > 0 && (
                  <div className="space-y-1 pb-2 border-b">
                    {payments.map((payment, index) => {
                      const account = paymentAccounts.find(a => a.id === payment.account_id);
                      const Icon = account ? ACCOUNT_TYPE_ICONS[account.type] || CreditCard : CreditCard;
                      return (
                        <div key={payment.id || payment.account_id || `${payment.display_label || 'payment'}-${payment.amount || '0'}-${index}`} className="flex items-center gap-2 bg-green-50 p-2 rounded text-sm">
                          <Icon className="h-4 w-4 text-green-600" />
                          <span className="flex-1 font-medium" data-testid={`selected-payment-label-${index}`}>{payment.display_label || payment.account_name}</span>
                          <Input
                            type="number"
                            min="0"
                            className="h-6 w-24 text-sm text-right"
                            value={payment.amount}
                            onChange={(e) => updatePaymentAmount(index, e.target.value)}
                          />
                          <span className="text-xs text-muted-foreground">MT</span>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={() => removePayment(index)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Add payment */}
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Select value={selectedPaymentAccount} onValueChange={setSelectedPaymentAccount}>
                      <SelectTrigger className="h-8 text-sm" data-testid="payment-account-select">
                        <SelectValue placeholder={t('salesPage.selectAccount')} />
                      </SelectTrigger>
                      <SelectContent>
                        {dedupedPaymentAccounts.filter(a => !payments.some(p => p.account_id === a.id)).map(acc => {
                          const Icon = ACCOUNT_TYPE_ICONS[acc.type] || CreditCard;
                          return (
                            <SelectItem key={acc.id} value={acc.id} data-testid={`payment-account-option-${acc.id}`}>
                              <div className="flex items-center gap-2">
                                <Icon className="h-4 w-4" />
                                {getPaymentDisplayLabel(acc)}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input
                    type="number"
                    min="0"
                    placeholder={t('salesPage.paid')}
                    className="h-8 w-24 text-sm"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    data-testid="payment-amount-input"
                  />
                  <Button size="sm" className="h-8" onClick={addPayment} data-testid="add-payment-button">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {selectedPaymentAccountData && !isSelectedPaymentCash && (
                  <p className="text-[11px] text-amber-700" data-testid="non-cash-exact-payment-hint">
                    {t('salesPage.exactPaymentHint')}
                  </p>
                )}
                {paymentSummary.hasCashPayment && paymentSummary.nonCashTotal > 0 && (
                  <p className="text-[11px] text-emerald-700" data-testid="mixed-payment-cash-change-hint">
                    {t('salesPage.mixedPaymentCashHint')}
                  </p>
                )}
                
                {/* Fill remaining */}
                {paymentSummary.remaining > 0 && (
                  <Button variant="link" size="sm" className="h-6 p-0 text-xs" onClick={fillRemainingPayment} data-testid="fill-remaining-payment-button">
                    {t('salesPage.fillRemaining', { amount: formatCurrency(paymentSummary.remaining) })}
                  </Button>
                )}
              </div>

              {/* Notes & Draft */}
              <div className="space-y-2">
                <Textarea placeholder={t('salesPage.optionalNotes')} className="text-sm h-16" value={saleNotes} onChange={(e) => setSaleNotes(e.target.value)} />
                
                <div className="space-y-1">
                  <Label className="flex items-center gap-2 text-xs">
                    <Clock className="h-3 w-3 text-amber-500" />
                    {t('salesPage.registeredAt')}
                  </Label>
                  <Input
                    type="datetime-local"
                    value={saleRegisteredAt}
                    onChange={(e) => setSaleRegisteredAt(e.target.value)}
                    max={new Date().toISOString().slice(0, 16)}
                    className="text-sm border-amber-200 focus:border-amber-400"
                  />
                </div>
                
                <div className="flex items-center space-x-2 p-2 border rounded bg-amber-50">
                  <Checkbox id="draft" checked={createAsDraft} onCheckedChange={setCreateAsDraft} />
                  <label htmlFor="draft" className="text-xs cursor-pointer">{t('salesPage.createDraft')}</label>
                </div>
              </div>

              {/* VAT/IVA Regime Selector */}
              <div className="p-3 border rounded-lg bg-blue-50 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Percent className="h-4 w-4" />
                    {t('salesPage.vatRegime')}
                  </Label>

      {/* Margin Override Dialog - P0-POS-01 */}
      <Dialog open={marginOverrideOpen} onOpenChange={(open) => { if (!open) handleCancelMarginOverride(); }}>
        <DialogContent className={`max-w-2xl ${profitNegative ? 'border-red-200 bg-red-50' : ''}`} data-testid="margin-override-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className={`h-5 w-5 ${profitNegative ? 'text-red-600' : 'text-amber-600'}`} />
              {profitNegative ? t('salesPage.lossSale') : t('salesPage.belowMarginSale')}
            </DialogTitle>
            <DialogDescription>
              {profitNegative
                ? t('salesPage.negativeProfitConfirmation')
                : t('salesPage.belowMarginConfirmation')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {(marginWarning?.warnings || []).map((warning, index) => (
              <div key={warning.product_id || warning.product_name || `${warning.actual_margin_pct || 'margin'}-${index}`} className="border rounded-lg p-3 bg-amber-50" data-testid={`margin-warning-row-${warning.product_id || index}`}>
                <div className="flex items-center justify-between">
                  <p className="font-medium">{warning.product_name}</p>
                  <Badge variant="outline" className="text-amber-700">
                    {t('salesPage.marginLabel')}: {warning.actual_margin_pct}% ({t('salesPage.minimumShort')} {warning.min_margin_pct}%)
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                  <div>
                    <p className="text-muted-foreground">{t('salesPage.salePrice')}</p>
                    <p className="font-medium">{formatCurrency(warning.sale_price)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('salesPage.minimumPrice')}</p>
                    <p className="font-medium">{formatCurrency(warning.min_price)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('salesPage.difference')}</p>
                    <p className={warning.price_diff < 0 ? 'font-medium text-red-600' : 'font-medium text-green-600'}>
                      {formatCurrency(warning.price_diff)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('salesPage.profit')}</p>
                    <p className={warning.profit_value < 0 ? 'font-medium text-red-600' : 'font-medium text-green-600'}>
                      {formatCurrency(warning.profit_value)} ({warning.profit_pct}%)
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('salesPage.quantity')}</p>
                    <p className="font-medium">{warning.qty}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('salesPage.lineTotal')}</p>
                    <p className="font-medium">{formatCurrency(warning.line_total)}</p>
                  </div>
                </div>
              </div>
            ))}

            <Alert className="border-amber-200 bg-amber-50" data-testid="margin-override-approval-alert">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-700">
                {t('salesPage.supervisorApprovalRequired')}
              </AlertDescription>
            </Alert>

            {profitNegative && (
              <Alert className="border-red-200 bg-red-50" data-testid="margin-override-negative-alert">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-700">
                  <strong>{t('salesPage.lossSaleStrong')}</strong> {t('salesPage.reinforcedConfirmationRequired')}
                </AlertDescription>
              </Alert>
            )}

            {requiresPin && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>{t('salesPage.authorizerName')}</Label>
                  <Select value={overrideAuthorizerId || '__none__'} onValueChange={(value) => setOverrideAuthorizerId(value === '__none__' ? '' : value)}>
                    <SelectTrigger data-testid="margin-override-authorizer-select">
                      <SelectValue placeholder={loadingOverrideAuthorizers ? t('salesPage.loadingAuthorizers') : t('salesPage.selectAuthorizer')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t('salesPage.selectAuthorizer')}</SelectItem>
                      {overrideAuthorizers.map((authorizer) => (
                        <SelectItem key={authorizer.id} value={authorizer.id}>
                          {authorizer.full_name} • {authorizer.role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t('salesPage.supervisorPin')}</Label>
                  <Input
                    type="password"
                    value={overridePin}
                    onChange={(e) => setOverridePin(e.target.value)}
                    placeholder={t('salesPage.enterPin')}
                    data-testid="margin-override-pin-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    {selectedOverrideAuthorizer
                      ? t('salesPage.authorizerSelectedHelp', { name: selectedOverrideAuthorizer.full_name })
                      : t('salesPage.authorizerRolesHelp')}
                  </p>
                </div>

                {!loadingOverrideAuthorizers && overrideAuthorizers.length === 0 && (
                  <Alert className="border-red-200 bg-red-50" data-testid="margin-override-no-authorizers-alert">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-700">
                      {t('salesPage.noCommercialAuthorizersConfigured')}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCancelMarginOverride} data-testid="margin-override-cancel-btn">
              {t('salesPage.goBackAdjust')}
            </Button>
            <Button
              onClick={handleConfirmMarginOverride}
              disabled={overrideProcessing}
              data-testid="margin-override-confirm-btn"
            >
              {overrideProcessing ? t('salesPage.processing') : t('salesPage.finalizeAnyway')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

                  <Select value={saleVatRegime} onValueChange={setSaleVatRegime}>
                    <SelectTrigger className="w-[160px]" data-testid="vat-regime-selector">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inclusive">{t('salesPage.vatIncluded')}</SelectItem>
                      <SelectItem value="exclusive">{t('salesPage.vatExcluded')}</SelectItem>
                      <SelectItem value="exempt">{t('salesPage.exempt')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-blue-700">
                  {t('salesPage.vatRateInfo', {
                    rate: vatConfig.default_rate,
                    mode: saleVatRegime === 'inclusive' ? t('salesPage.priceIncludesVat') : saleVatRegime === 'exclusive' ? t('salesPage.vatWillBeAdded') : t('salesPage.noVat')
                  })}
                </p>
              </div>

              {/* Totals with VAT breakdown */}
              <div className="p-3 border rounded-lg bg-slate-50 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>{t('salesPage.subtotalGross')}</span>
                  <span>{formatCurrency(calculateSubtotal())}</span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>{t('salesPage.itemDiscountShort')}</span>
                  <span>-{formatCurrency(calculateItemsDiscount())}</span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>{t('salesPage.saleDiscountShort')}</span>
                  <span>-{formatCurrency(calculateSaleDiscount())}</span>
                </div>
                <div className="border-t pt-1 mt-1">
                  <div className="flex justify-between text-muted-foreground">
                    <span>{t('salesPage.taxableBase')}</span>
                    <span>{formatCurrency(calculateCartVAT().base)}</span>
                  </div>
                  <div className="flex justify-between text-primary">
                    <span>IVA ({vatConfig.default_rate}%)</span>
                    <span>{formatCurrency(calculateCartVAT().vat)}</span>
                  </div>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>{t('salesPage.totalUpper')}</span>
                  <span>{formatCurrency(calculateTotal())}</span>
                </div>
                <div className="flex justify-between text-sm" data-testid="pos-preview-total-cost-row">
                  <span>{t('salesPage.totalCostPreview')}</span>
                  <span>{formatCurrency(cartMarginPreview.totalCost || 0)}</span>
                </div>
                <div className="flex justify-between text-sm" data-testid="pos-preview-total-profit-row">
                  <span>{t('salesPage.totalProfitPreview')}</span>
                  <span>{cartMarginPreview.totalProfit == null ? 'N/A' : formatCurrency(cartMarginPreview.totalProfit)}</span>
                </div>
                <div className="flex justify-between text-sm" data-testid="pos-preview-total-margin-row">
                  <span>{t('salesPage.totalMarginPreview')}</span>
                  <span>{(cartMarginPreview.marginConfidence !== 'ok' || cartMarginPreview.totalMarginPct == null) ? 'N/A' : `${Number(cartMarginPreview.totalMarginPct).toFixed(2)}%`}</span>
                </div>
                {cartMarginPreview.marginConfidence !== 'ok' && (
                  <div className="pt-1" data-testid="pos-margin-not-reliable-badge-wrapper">
                    <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid="pos-margin-not-reliable-badge">
                      {t('salesPage.unreliableMargin')}
                    </Badge>
                    <p className="text-[11px] text-amber-700 mt-1" data-testid="pos-margin-not-reliable-tooltip">
                      {t('salesPage.unreliableMarginHint')}
                    </p>
                  </div>
                )}
                <div className="flex justify-between text-indigo-700 font-medium">
                  <span>{t('salesPage.paidUpper')}</span>
                  <span>{formatCurrency(paymentSummary.totalPaid)}</span>
                </div>
                {paymentSummary.nonCashTotal > 0 && (
                  <div className="flex justify-between text-sm text-slate-700" data-testid="non-cash-paid-preview-row">
                    <span>{t('salesPage.nonCashPaid')}</span>
                    <span>{formatCurrency(paymentSummary.nonCashTotal)}</span>
                  </div>
                )}
                {paymentSummary.cashTotal > 0 && (
                  <div className="flex justify-between text-sm text-slate-700" data-testid="cash-paid-preview-row">
                    <span>{t('salesPage.cashPaid')}</span>
                    <span>{formatCurrency(paymentSummary.cashTotal)}</span>
                  </div>
                )}
                {paymentSummary.change > 0 && (
                  <div className="flex justify-between font-medium text-green-700" data-testid="cash-change-preview-row">
                    <span>{t('salesPage.changeUpper')}</span>
                    <span>{formatCurrency(paymentSummary.change)}</span>
                  </div>
                )}
                {paymentSummary.remaining > 0 && (
                  <div className="flex justify-between font-medium text-red-600" data-testid="payment-remaining-preview-row">
                    <span>{t('salesPage.remaining')}</span>
                    <span>{formatCurrency(paymentSummary.remaining)}</span>
                  </div>
                )}
                {paymentSummary.nonCashOverpay > 0 && (
                  <div className="flex justify-between font-medium text-red-600" data-testid="non-cash-payment-mismatch-row">
                    <span>{t('salesPage.nonCashOverflow')}</span>
                    <span>{formatCurrency(paymentSummary.nonCashOverpay)}</span>
                  </div>
                )}
              </div>

              {!createAsDraft && selectedLocation && !activeCashSession?.id && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="pos-open-cash-cta-box">
                  <p className="text-sm text-amber-900">
                    {t('salesPage.noOpenCash', { location: selectedLocation.name })}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    className="mt-2"
                    onClick={handleOpenCashFromPos}
                    data-testid="pos-open-cash-cta-button"
                  >
                    {t('salesPage.openCashNow')}
                  </Button>
                </div>
              )}

              <Button 
                onClick={handleCheckout} 
                className="w-full h-12 text-lg" 
                disabled={cart.length === 0 || processing || !selectedEmployee || payments.length === 0}
              >
                {processing ? t('salesPage.processing') : createAsDraft ? t('salesPage.saveDraft') : t('salesPage.finalizeSale')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Item Selection Dialog */}
      <Dialog open={itemSelectDialogOpen} onOpenChange={setItemSelectDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Seleccionar Item - {pendingProduct?.name}</DialogTitle>
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <span>Items disponíveis em</span>
              <Badge variant="outline"><MapPin className="h-3 w-3 mr-1" />{selectedLocation?.name}</Badge>
            </div>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
              <AlertCircle className="h-4 w-4 text-amber-600 inline mr-2" />
              Este produto requer selecção de uma unidade específica com IMEI/Número de Série.
            </div>

            {loadingItems ? (
              <div className="text-center py-8 text-muted-foreground">A carregar...</div>
            ) : availableItems.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>Nenhum item disponível em {selectedLocation?.name}</p>
                <p className="text-xs mt-1">Verifique outras localizações ou adicione stock</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {availableItems.map((item) => (
                  <div
                    key={item.id}
                    className={cn(
                      "flex items-center justify-between border rounded-lg p-3 cursor-pointer transition-colors",
                      selectedItemForSale?.id === item.id ? "border-indigo-500 bg-indigo-50" : "hover:bg-slate-50",
                      cart.some(c => c.item_id === item.id) && "opacity-50 cursor-not-allowed"
                    )}
                    onClick={() => {
                      if (!cart.some(c => c.item_id === item.id)) {
                        setSelectedItemForSale(item);
                      }
                    }}
                  >
                    <div>
                      <p className="font-mono font-medium">{item.imei1 || item.serial_number}</p>
                      <div className="flex gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">{item.condition === 'new' ? 'Novo' : item.condition}</Badge>
                        <Badge variant="outline" className="text-xs">{item.location}</Badge>
                      </div>
                    </div>
                    {selectedItemForSale?.id === item.id && <Check className="h-5 w-5 text-indigo-600" />}
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => { setItemSelectDialogOpen(false); setPendingProduct(null); setSelectedItemForSale(null); }}>Cancelar</Button>
              <Button onClick={() => selectedItemForSale && handleItemSelect(selectedItemForSale)} disabled={!selectedItemForSale}>Adicionar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Sale Dialog */}
      <Dialog open={viewSaleDialogOpen} onOpenChange={setViewSaleDialogOpen}>
        <DialogContent className="w-[min(96vw,1040px)] max-w-[1040px] max-h-[92vh] overflow-y-auto p-3 sm:p-4" data-testid="view-sale-dialog">
          <DialogHeader>
            <DialogTitle>Venda - {formatDisplayOnlyCode(selectedSale?.sale_number, 'SAL')}</DialogTitle>
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <span>Venda efectuada em:</span>
              <Badge variant="outline"><MapPin className="h-3 w-3 mr-1" />{selectedSale?.location_name || 'N/A'}</Badge>
            </div>
          </DialogHeader>
          
          {selectedSale && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <div>
                  <p className="text-sm text-muted-foreground">{t('salesPage.statusLabel')}</p>
                  <Badge className={statusColors[selectedSale.status]} variant="outline">
                    {statusLabels[selectedSale.status]}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('salesPage.locationShort')}</p>
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4 text-primary" />
                    <p className="font-medium">{selectedSale.location_name || 'N/A'}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('salesPage.employeeShort')}</p>
                  <p className="font-medium">{selectedSale.employee_name || selectedSale.seller_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('labels.customer')}</p>
                  <p className="font-medium" data-testid="sale-detail-client-name">{selectedSale.customer_name || 'Anónimo'}</p>
                  <p className="text-xs text-muted-foreground" data-testid="sale-detail-client-code">
                    {t('salesPage.customerCode')}: {selectedSale?.customer_snapshot?.code || selectedSale?.customer_snapshot?.client_code || 'N/A'}
                  </p>
                </div>
              </div>

              {(selectedSale.payment_type === 'credit' || selectedSale.fiscal_document_type === 'factura' || selectedSale.ar_id) && (
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(`/receivables?sale_id=${selectedSale.id}&sale_number=${selectedSale.sale_number || ''}`, '_blank')}
                    data-testid="open-sale-ar-link-button"
                  >
                    {t('salesPage.openReceivable')}
                  </Button>
                </div>
              )}

              {(selectedSale.source_quote_id || selectedSale.quotation_id) && (
                <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg border bg-indigo-50" data-testid="sale-source-quotation-link-block">
                  <span className="text-sm text-indigo-700">{t('salesPage.originatedQuote')}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(`/documents?quotation_id=${selectedSale.source_quote_id || selectedSale.quotation_id}`, '_blank')}
                    data-testid="sale-open-source-quotation-link"
                  >
                    {selectedSale.source_quote_number || selectedSale.quotation_number || selectedSale.source_quote_id || selectedSale.quotation_id}
                  </Button>
                </div>
              )}

              {selectedSale.source_project_id && (
                <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg border bg-emerald-50" data-testid="sale-source-project-link-block">
                  <span className="text-sm text-emerald-700">{t('salesPage.originatedProject')}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(`/services?module=obras&project_id=${selectedSale.source_project_id}`, '_blank')}
                    data-testid="sale-open-source-project-link"
                  >
                    {selectedSale.source_project_number || selectedSale.source_project_id}
                  </Button>
                  {selectedSale.source_project_title && <span className="text-sm text-emerald-900">{selectedSale.source_project_title}</span>}
                </div>
              )}
              
              <div className="grid gap-3 lg:grid-cols-2">
              {/* Items */}
              <div className="border rounded-lg p-3 lg:col-span-2">
                <h4 className="font-medium mb-3">{t('salesPage.itemsSection')}</h4>
                <table className="w-full text-xs">
                  <thead className="border-b">
                    <tr>
                      <th className="text-left py-2">{t('salesPage.productLabel')}</th>
                      <th className="text-right py-2">{t('salesPage.qtyLabelShort')}</th>
                      <th className="text-right py-2">{t('salesPage.priceLabel')}</th>
                      <th className="text-right py-2">{t('salesPage.discountLabelShort')}</th>
                      <th className="text-right py-2">{t('salesPage.costLabel')}</th>
                      <th className="text-right py-2">{t('salesPage.profitLabelShort')}</th>
                      <th className="text-right py-2">{t('salesPage.marginLabelShort')}</th>
                      <th className="text-right py-2">{t('salesPage.totalUpper')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedSale.items?.map((item, i) => {
                      const itemDiscount = (item.discount || 0) + ((item.quantity * item.unit_price) * ((item.discount_percent || 0) / 100));
                      return (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-2">
                            {item.product_name}
                            {item.description && item.description !== item.product_name && (
                              <p className="text-xs text-slate-600" data-testid={`sale-detail-item-description-${i}`}>
                                {item.description}
                              </p>
                            )}
                            {(item.is_kit || item.kit_id || item.item_type === 'kit') && (
                              <p className="text-xs text-emerald-700" data-testid={`sale-detail-kit-line-${i}`}>
                                {t('salesPage.includesComponents', { count: (item.kit_components_snapshot || []).length })} • {item.kit_code || 'KIT'}
                              </p>
                            )}
                            {item.serial_number && <p className="text-xs font-mono text-indigo-600">{item.serial_number}</p>}
                          </td>
                          <td className="text-right py-2">{item.quantity}</td>
                          <td className="text-right py-2">{formatCurrency(item.unit_price)}</td>
                          <td className="text-right py-2 text-green-600">-{formatCurrency(itemDiscount)}</td>
                          <td className="text-right py-2" data-testid={`sale-detail-item-line-cost-${i}`}>
                            {item.line_cost == null ? 'N/A' : formatCurrency(item.line_cost)}
                          </td>
                          <td className="text-right py-2" data-testid={`sale-detail-item-line-profit-${i}`}>
                            {item.line_profit == null ? 'N/A' : formatCurrency(item.line_profit)}
                          </td>
                          <td className="text-right py-2" data-testid={`sale-detail-item-line-margin-${i}`}>
                            {item.line_margin_pct == null ? 'N/A' : `${Number(item.line_margin_pct).toFixed(2)}%`}
                          </td>
                          <td className="text-right py-2 font-medium">{formatCurrency((item.quantity * item.unit_price) - itemDiscount)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Payments */}
              <div className="border rounded-lg p-3">
                <h4 className="font-medium mb-3" data-testid="sale-detail-payment-form-label">{t('salesPage.paymentSection')}</h4>
                <div className="space-y-2">
                  {selectedSale.payments?.map((payment, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span>{t('salesPage.paymentFormLine')} {payment.account_name}</span>
                      <span className="font-medium">{formatCurrency(payment.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Totals */}
              <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span>{t('salesPage.subtotal')}</span>
                  <span>{formatCurrency(selectedSale.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm text-green-600">
                  <span>{t('salesPage.itemsDiscount')}</span>
                  <span>-{formatCurrency(selectedSale.items_discount || 0)}</span>
                </div>
                <div className="flex justify-between text-sm text-green-600">
                  <span>{t('salesPage.saleDiscountWithPct', { pct: selectedSale.sale_discount_percent || 0 })}</span>
                  <span>-{formatCurrency(selectedSale.sale_discount || 0)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>{t('salesPage.totalUpper')}</span>
                  <span className="text-green-600">{formatCurrency(selectedSale.total_amount)}</span>
                </div>
                <div className="flex justify-between text-sm" data-testid="sale-detail-total-cost-row">
                  <span>{t('salesPage.totalCost')}</span>
                  <span>{formatCurrency(selectedSale.total_cost || 0)}</span>
                </div>
                <div className="flex justify-between text-sm" data-testid="sale-detail-total-profit-row">
                  <span>{t('salesPage.totalProfit')}</span>
                  <span>{selectedSale.total_profit == null ? 'N/A' : formatCurrency(selectedSale.total_profit)}</span>
                </div>
                <div className="flex justify-between text-sm" data-testid="sale-detail-total-margin-row">
                  <span>{t('salesPage.totalMargin')}</span>
                  <span>{(selectedSale.margin_confidence !== 'ok' || selectedSale.total_margin_pct == null) ? 'N/A' : `${Number(selectedSale.total_margin_pct).toFixed(2)}%`}</span>
                </div>
                {selectedSale.margin_confidence !== 'ok' && (
                  <div className="pt-1" data-testid="sale-detail-margin-not-reliable-wrapper">
                    <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid="sale-detail-margin-not-reliable-badge">
                      {t('salesPage.unreliableMargin')}
                    </Badge>
                  </div>
                )}
              </div>
              </div>
              
              {/* Print Options */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Printer className="h-4 w-4" />
                  {t('salesPage.printOptions')}
                </h4>

                {selectedSale.status === 'cancelled' ? (
                  <div className="grid grid-cols-1 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePrintCancelledSale(selectedSale)}
                      data-testid="view-sale-print-cancelled-btn"
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      {t('salesPage.printCancelledDocument')}
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleViewPrint('pos_receipt')}
                      data-testid="view-sale-print-pos-btn"
                    >
                      <Receipt className="h-4 w-4 mr-1" />
                      POS
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleViewPrint('invoice')}
                      data-testid="view-sale-print-invoice-btn"
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      {t('salesPage.invoice')}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleViewPrint('delivery_guide')}
                      data-testid="view-sale-print-delivery-btn"
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      {t('salesPage.deliveryGuide')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Criar Cliente Rápido */}
      <Dialog open={clientCreateOpen} onOpenChange={setClientCreateOpen}>
        <DialogContent data-testid="client-create-modal">
          <DialogHeader>
            <DialogTitle>{t('salesPage.newClientTitle')}</DialogTitle>
            <DialogDescription>{t('salesPage.newClientDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>{t('salesPage.nameRequired')}</Label>
              <Input
                value={clientForm.name}
                onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                data-testid="client-create-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('labels.phone')}</Label>
                <Input
                  value={clientForm.phone}
                  onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                  data-testid="client-create-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('labels.email')}</Label>
                <Input
                  value={clientForm.email}
                  onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                  data-testid="client-create-email-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('salesPage.taxId')}</Label>
                <Input
                  value={clientForm.nuit}
                  onChange={(e) => setClientForm({ ...clientForm, nuit: e.target.value })}
                  data-testid="client-create-nuit-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('salesPage.typeLabel')}</Label>
                <Select
                  value={clientForm.client_type}
                  onValueChange={(value) => setClientForm({ ...clientForm, client_type: value })}
                >
                  <SelectTrigger data-testid="client-create-type-select">
                    <SelectValue placeholder={t('salesPage.typeLabel')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">{t('salesPage.individual')}</SelectItem>
                    <SelectItem value="company">{t('salesPage.company')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('salesPage.addressLabel')}</Label>
                <Input
                  value={clientForm.address}
                  onChange={(e) => setClientForm({ ...clientForm, address: e.target.value })}
                  data-testid="client-create-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('salesPage.cityLabel')}</Label>
                <Input
                  value={clientForm.city}
                  onChange={(e) => setClientForm({ ...clientForm, city: e.target.value })}
                  data-testid="client-create-city-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('salesPage.contactPerson')}</Label>
              <Input
                value={clientForm.contact_person}
                onChange={(e) => setClientForm({ ...clientForm, contact_person: e.target.value })}
                data-testid="client-create-contact-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setClientCreateOpen(false)}>{t('actions.cancel')}</Button>
            <Button onClick={handleCreateClient} disabled={creatingClient} data-testid="client-create-submit-btn">
              {creatingClient ? t('salesPage.creating') : t('salesPage.createClient')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Sale Dialog */}
      <Dialog open={editSaleDialogOpen} onOpenChange={setEditSaleDialogOpen}>
        <DialogContent className="w-[min(96vw,1100px)] max-w-[1100px] max-h-[92vh] overflow-y-auto p-4 sm:p-5" data-testid="edit-sale-dialog">
          <DialogHeader>
            <DialogTitle>{t('salesPage.editSaleTitle', { sale: formatDisplayOnlyCode(selectedSale?.sale_number, 'SAL') })}</DialogTitle>
            <DialogDescription data-testid="edit-sale-status-helper">
              {canFullyEditSelectedSale ? t('salesPage.editSaleFullAccess') : t('salesPage.editableProjectValuesDesc')}
            </DialogDescription>
          </DialogHeader>
          
          {editSaleForm && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm" data-testid="edit-sale-alert-card">
                <AlertCircle className="h-4 w-4 text-amber-600 inline mr-2" />
                {t('salesPage.changesTracked')}
              </div>

              <div className="grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
                <div className="space-y-4">
                  <div className="rounded-xl border bg-slate-50 p-4 space-y-3" data-testid="edit-sale-customer-section">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{t('salesPage.customerBlockTitle')}</p>
                        <p className="text-xs text-slate-500">{t('salesPage.customerBlockDescription')}</p>
                      </div>
                      <Badge variant="outline" data-testid="edit-sale-current-status-badge">{statusLabels[selectedSale?.status] || selectedSale?.status}</Badge>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="edit-sale-customer-name">{t('salesPage.customerNameLabel')}</Label>
                      <Input id="edit-sale-customer-name" value={editSaleForm.customer_name} onChange={(e) => updateEditSaleField('customer_name', e.target.value)} data-testid="edit-sale-customer-name-input" />
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="edit-sale-customer-phone">{t('labels.phone')}</Label>
                        <Input id="edit-sale-customer-phone" value={editSaleForm.customer_phone} onChange={(e) => updateEditSaleField('customer_phone', e.target.value)} data-testid="edit-sale-customer-phone-input" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-sale-customer-email">{t('labels.email')}</Label>
                        <Input id="edit-sale-customer-email" value={editSaleForm.customer_email} onChange={(e) => updateEditSaleField('customer_email', e.target.value)} data-testid="edit-sale-customer-email-input" />
                      </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="edit-sale-customer-nuit">{t('salesPage.taxId')}</Label>
                        <Input id="edit-sale-customer-nuit" value={editSaleForm.customer_nuit} onChange={(e) => updateEditSaleField('customer_nuit', e.target.value)} data-testid="edit-sale-customer-nuit-input" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-sale-customer-city">{t('salesPage.cityLabel')}</Label>
                        <Input id="edit-sale-customer-city" value={editSaleForm.customer_city} onChange={(e) => updateEditSaleField('customer_city', e.target.value)} data-testid="edit-sale-customer-city-input" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="edit-sale-customer-address">{t('salesPage.addressLabel')}</Label>
                      <Input id="edit-sale-customer-address" value={editSaleForm.customer_address} onChange={(e) => updateEditSaleField('customer_address', e.target.value)} data-testid="edit-sale-customer-address-input" />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="edit-sale-contact-person">{t('salesPage.contactPerson')}</Label>
                      <Input id="edit-sale-contact-person" value={editSaleForm.customer_contact_person} onChange={(e) => updateEditSaleField('customer_contact_person', e.target.value)} data-testid="edit-sale-customer-contact-input" />
                    </div>
                  </div>

                  <div className="rounded-xl border bg-white p-4 space-y-3" data-testid="edit-sale-items-section">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{t('salesPage.editSaleItemsTitle')}</p>
                      <p className="text-xs text-slate-500">{t('salesPage.editSaleItemsDescription')}</p>
                    </div>

                    <div className="space-y-3">
                      {(editSaleForm.items || []).map((item, index) => {
                        const quantity = Math.max(1, Number(item.quantity) || 1);
                        const unitPrice = Number(item.unit_price) || 0;
                        const discountValue = Number(item.discount) || 0;
                        const discountPercent = Number(item.discount_percent) || 0;
                        const lineAmount = quantity * unitPrice;
                        const lineDiscount = discountValue + (lineAmount * (discountPercent / 100));
                        const lineNet = Math.max(lineAmount - lineDiscount, 0);
                        return (
                          <div key={`${item.product_id}-${index}`} className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid={`edit-sale-line-${index}`}>
                            <div className="mb-3 flex items-start justify-between gap-3">
                              <div>
                                <p className="font-medium text-slate-900">{item.product_name}</p>
                                <p className="text-xs text-slate-500">{item.serial_number || item.item_id || t('salesPage.noAdditionalDescription')}</p>
                              </div>
                              <Badge variant="outline" data-testid={`edit-sale-line-total-${index}`}>{formatCurrency(lineNet)}</Badge>
                            </div>

                            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                              <div className="space-y-2">
                                <Label>{t('salesPage.qtyLabelShort')}</Label>
                                <Input type="number" min="1" value={item.quantity} onChange={(e) => updateEditSaleLineField(index, 'quantity', e.target.value)} data-testid={`edit-sale-line-quantity-${index}`} />
                              </div>
                              <div className="space-y-2">
                                <Label>{t('salesPage.priceLabel')}</Label>
                                <Input type="number" min="0" step="0.01" value={item.unit_price} onChange={(e) => updateEditSaleLineField(index, 'unit_price', e.target.value)} data-testid={`edit-sale-line-unit-price-${index}`} />
                              </div>
                              <div className="space-y-2">
                                <Label>{t('salesPage.valueDiscount')}</Label>
                                <Input type="number" min="0" step="0.01" value={item.discount} onChange={(e) => updateEditSaleLineField(index, 'discount', e.target.value)} data-testid={`edit-sale-line-discount-${index}`} />
                              </div>
                              <div className="space-y-2">
                                <Label>{t('salesPage.percentDiscount')}</Label>
                                <Input type="number" min="0" max="100" step="0.01" value={item.discount_percent} onChange={(e) => updateEditSaleLineField(index, 'discount_percent', e.target.value)} data-testid={`edit-sale-line-discount-percent-${index}`} />
                              </div>
                              <div className="space-y-2">
                                <Label>{t('salesPage.vatRegime')}</Label>
                                <Select value={item.vat_regime || 'inclusive'} onValueChange={(value) => updateEditSaleLineField(index, 'vat_regime', value)}>
                                  <SelectTrigger data-testid={`edit-sale-line-vat-regime-${index}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="inclusive">IVA Incluído</SelectItem>
                                    <SelectItem value="exclusive">IVA Acrescido</SelectItem>
                                    <SelectItem value="exempt">Isento</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            <div className="mt-3 grid gap-3 md:grid-cols-[1fr_120px]">
                              <div className="space-y-2">
                                <Label>{t('labels.description')}</Label>
                                <Input value={item.description || ''} onChange={(e) => updateEditSaleLineField(index, 'description', e.target.value)} data-testid={`edit-sale-line-description-${index}`} />
                              </div>
                              <div className="space-y-2">
                                <Label>IVA (%)</Label>
                                <Input type="number" min="0" step="0.01" value={item.vat_rate} onChange={(e) => updateEditSaleLineField(index, 'vat_rate', e.target.value)} data-testid={`edit-sale-line-vat-rate-${index}`} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-xl border bg-white p-4 space-y-3" data-testid="edit-sale-financial-section">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{t('salesPage.editSaleFinancialTitle')}</p>
                      <p className="text-xs text-slate-500">{t('salesPage.editSaleFinancialDescription')}</p>
                    </div>

                    <div className="space-y-2">
                      <Label>{t('salesPage.employeeForSale')}</Label>
                      <Select value={editSaleForm.employee_id || ''} onValueChange={(value) => updateEditSaleField('employee_id', value)}>
                        <SelectTrigger data-testid="edit-sale-employee-select">
                          <SelectValue placeholder={t('salesPage.selectEmployee')} />
                        </SelectTrigger>
                        <SelectContent>
                          {employees.map((emp) => (
                            <SelectItem key={emp.id} value={emp.id} data-testid={`edit-sale-employee-option-${emp.id}`}>{emp.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>{t('salesPage.valueDiscount')}</Label>
                        <Input type="number" min="0" step="0.01" value={editSaleForm.sale_discount} onChange={(e) => updateEditSaleField('sale_discount', e.target.value)} data-testid="edit-sale-sale-discount-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('salesPage.percentDiscount')}</Label>
                        <Input type="number" min="0" max="100" step="0.01" value={editSaleForm.sale_discount_percent} onChange={(e) => updateEditSaleField('sale_discount_percent', e.target.value)} data-testid="edit-sale-sale-discount-percent-input" />
                      </div>
                    </div>

                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 space-y-2" data-testid="edit-sale-summary-totals-card">
                      <div className="flex items-center justify-between text-sm">
                        <span>{t('salesPage.subtotal')}</span>
                        <span>{formatCurrency(editSaleTotals.base + editSaleTotals.vat)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span>{t('salesPage.paymentSection')}</span>
                        <span>{formatCurrency(editSalePaymentsTotal)}</span>
                      </div>
                      <div className="flex items-center justify-between text-base font-semibold border-t border-emerald-200 pt-2">
                        <span>{t('salesPage.editSaleComputedTotal')}</span>
                        <span className="text-emerald-700" data-testid="edit-sale-computed-total-value">{formatCurrency(editSaleTotals.total)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border bg-white p-4 space-y-3" data-testid="edit-sale-payments-section">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{t('salesPage.paymentSection')}</p>
                      <p className="text-xs text-slate-500">{t('salesPage.editSalePaymentsDescription')}</p>
                    </div>

                    <div className="space-y-2">
                      {(editSaleForm.payments || []).map((payment, index) => (
                        <div key={`${payment.account_id}-${index}`} className="flex items-center gap-2 rounded-lg border bg-slate-50 p-2" data-testid={`edit-sale-payment-row-${index}`}>
                          <span className="flex-1 text-sm font-medium">{payment.display_label || payment.account_name}</span>
                          <Input type="number" min="0" step="0.01" className="w-28" value={payment.amount} onChange={(e) => updateEditSalePaymentAmount(index, e.target.value)} data-testid={`edit-sale-payment-amount-${index}`} />
                          <Button size="icon" variant="ghost" onClick={() => removeEditSalePayment(index)} data-testid={`edit-sale-payment-remove-${index}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>

                    <div className="grid gap-2 sm:grid-cols-[1fr_120px_auto]">
                      <Select value={editSalePaymentAccount || ''} onValueChange={setEditSalePaymentAccount}>
                        <SelectTrigger data-testid="edit-sale-payment-account-select">
                          <SelectValue placeholder={t('salesPage.selectAccount')} />
                        </SelectTrigger>
                        <SelectContent>
                          {availableEditPaymentAccounts.map((account) => (
                            <SelectItem key={account.id} value={account.id} data-testid={`edit-sale-payment-account-option-${account.id}`}>{getPaymentDisplayLabel(account)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input type="number" min="0" step="0.01" value={editSalePaymentAmount} onChange={(e) => setEditSalePaymentAmount(e.target.value)} placeholder={t('salesPage.amountMt')} data-testid="edit-sale-payment-amount-input" />
                      <Button onClick={addEditSalePayment} data-testid="edit-sale-add-payment-button">
                        <Plus className="h-4 w-4 mr-1" />
                        {t('salesPage.addPaymentLabel')}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2 rounded-xl border bg-white p-4" data-testid="edit-sale-notes-section">
                    <Label>{t('labels.notes')}</Label>
                    <Textarea value={editSaleForm.notes} onChange={(e) => updateEditSaleField('notes', e.target.value)} data-testid="edit-sale-notes-input" />
                  </div>
                </div>
              </div>
              
              <div className="space-y-2 border-t pt-4">
                <Label className="text-amber-700 font-medium">{t('salesPage.justificationRequiredLabel')}</Label>
                <Textarea
                  value={editSaleJustification}
                  onChange={(e) => setEditSaleJustification(e.target.value)}
                  placeholder={t('salesPage.changeReasonPlaceholder')}
                  className="border-amber-300"
                  data-testid="edit-sale-justification-input"
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setEditSaleDialogOpen(false)} data-testid="edit-sale-cancel-button">{t('actions.cancel')}</Button>
                <Button onClick={handleSaveEditSale} disabled={!editSaleJustification || editSaleJustification.trim().length < 5} data-testid="edit-sale-save-button">{t('salesPage.save')}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={purgeCancelledDialog.open} onOpenChange={(open) => setPurgeCancelledDialog({ open, sale: open ? purgeCancelledDialog.sale : null })}>
        <AlertDialogContent data-testid="cancelled-sales-purge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-600" />
              {t('salesPage.purgeCancelledTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {purgeCancelledDialog.sale
                ? t('salesPage.purgeOneDescription', { sale: formatDisplayOnlyCode(purgeCancelledDialog.sale?.sale_number, 'SAL') })
                : t('salesPage.purgeAllDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancelled-sales-purge-cancel-button">{t('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePurgeCancelledSales}
              disabled={adminActionLoading}
              className="bg-red-600 hover:bg-red-700"
              data-testid="cancelled-sales-purge-confirm-button"
            >
              {adminActionLoading ? t('salesPage.deleting') : t('salesPage.confirmDelete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm Edit Dialog */}
      <AlertDialog open={confirmEditDialogOpen} onOpenChange={setConfirmEditDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('salesPage.confirmChangeTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('salesPage.changeWillBeTracked')}
              <p className="mt-2"><strong>{t('salesPage.justificationLabel')}</strong> &ldquo;{editSaleJustification}&rdquo;</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmEditSale}>{t('actions.confirm')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Sale Dialog */}
      <AlertDialog open={cancelSaleDialogOpen} onOpenChange={setCancelSaleDialogOpen}>
        <AlertDialogContent data-testid="cancel-sale-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-600" />
              {t('salesPage.cancelSaleTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>{t('salesPage.cancelSaleTitle')} <strong>{formatDisplayOnlyCode(selectedSale?.sale_number, 'SAL')}</strong>?</p>
              <p className="text-sm">{t('labels.amount')}: {formatCurrency(selectedSale?.total_amount || 0)}</p>
              
              {selectedSale?.status !== 'draft' && (
                <div className="flex items-center space-x-2 p-3 border rounded bg-amber-50">
                  <Checkbox id="revert-stock" checked={revertStockOnCancel} onCheckedChange={setRevertStockOnCancel} data-testid="cancel-sale-revert-stock-checkbox" />
                  <label htmlFor="revert-stock" className="text-sm cursor-pointer">{t('salesPage.returnItemsToStock')}</label>
                </div>
              )}
              
              <div className="space-y-2">
                <Label className="text-red-700 font-medium">{t('salesPage.overrideReason')}</Label>
                <Textarea
                  value={cancelSaleReason}
                  onChange={(e) => setCancelSaleReason(e.target.value)}
                  placeholder={t('salesPage.cancellationReasonPlaceholder')}
                  className="border-red-300"
                  data-testid="cancel-sale-reason-input"
                />
              </div>

              <div className="space-y-2">
                <Label>{t('salesPage.additionalNotes')}</Label>
                <Textarea
                  value={cancelSaleNotes}
                  onChange={(e) => setCancelSaleNotes(e.target.value)}
                  placeholder={t('salesPage.additionalNotesPlaceholder')}
                  data-testid="cancel-sale-notes-input"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-sale-dialog-close-button">{t('salesPage.back')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancelSale}
              className="bg-red-600 hover:bg-red-700"
              disabled={!cancelSaleReason || cancelSaleReason.trim().length < 1}
              data-testid="cancel-sale-confirm-button"
            >
              {t('salesPage.cancelSaleTitle')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Audit Dialog */}
      <Dialog open={auditDialogOpen} onOpenChange={setAuditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('salesPage.historyTitle', { sale: formatDisplayOnlyCode(selectedSale?.sale_number, 'SAL') })}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3">
            {auditLogs.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">{t('salesPage.noRecords')}</p>
            ) : (
              auditLogs.map((log) => (
                <div key={log.id} className="rounded-lg border bg-white p-3 shadow-sm" data-testid={`sales-history-row-${log.id}`}>
                  <div className="flex justify-between items-start mb-2">
                    <Badge variant="outline" className={cn(
                      log.action === 'created' && 'bg-green-50 text-green-700',
                      log.action === 'edited' && 'bg-amber-50 text-amber-700',
                      log.action === 'confirmed' && 'bg-blue-50 text-blue-700',
                      log.action === 'paid' && 'bg-green-50 text-green-700',
                      log.action === 'completed' && 'bg-emerald-50 text-emerald-700',
                      log.action === 'cancelled' && 'bg-red-50 text-red-700'
                    )}>
                      {log.action === 'created' && t('salesPage.createdStatus')}
                      {log.action === 'edited' && t('salesPage.editedStatus')}
                      {log.action === 'confirmed' && t('salesPage.confirmedStatus')}
                      {log.action === 'paid' && t('salesPage.paidStatus')}
                      {log.action === 'completed' && t('salesPage.completedStatus')}
                      {log.action === 'cancelled' && t('salesPage.cancelledStatus')}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      <Clock className="h-3 w-3 inline mr-1" />
                      {formatDateTime(log.timestamp)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm mb-2">
                    <User className="h-4 w-4 text-slate-500" />
                    <span>{log.user_name}</span>
                    <Badge variant="secondary" className="text-xs">{log.user_role}</Badge>
                  </div>
                  
                  {log.details && <p className="text-sm text-slate-600 mb-2">{log.details}</p>}
                  
                  {log.changes && log.changes.length > 0 && (
                    <div className="bg-slate-50 rounded p-2 mb-2 text-xs">
                      {log.changes.map((change, i) => (
                        <div key={i} className="flex gap-2">
                          <span className="font-medium">{change.field_label}:</span>
                          <span className="text-red-600 line-through">{String(change.old_value || '-')}</span>
                          <span>→</span>
                          <span className="text-green-600">{String(change.new_value || '-')}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {(log.justification || log.cancel_reason) && (
                    <div className={cn("p-2 rounded text-sm", log.cancel_reason ? "bg-red-50" : "bg-amber-50")}>
                      <strong>{log.cancel_reason ? t('salesPage.reasonLabel') : t('salesPage.justificationLabel')}</strong> &ldquo;{log.justification || log.cancel_reason}&rdquo;
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Missing Master Data Dialog (Impressão) */}
      <MissingMasterDataDialog
        open={printValidationState.open}
        onOpenChange={(value) =>
          setPrintValidationState(prev =>
            value
              ? { ...prev, open: true }
              : { open: false, warnings: [], blob: null, filename: '', sale: null, docType: null }
          )
        }
        warnings={printValidationState.warnings}
        customerName={printValidationState.sale?.customer_name || 'Cliente'}
        onEditClient={handleEditClientFromPrint}
        onPrintAnyway={handleMissingDataPrint}
      />
      
      {/* Print Dialog - Aparece após completar venda */}
      <SalePrintDialog
        open={printDialogOpen}
        onOpenChange={setPrintDialogOpen}
        sale={completedSale}
        onClose={() => {
          setCompletedSale(null);
          loadSales();
        }}
      />
      
      {/* Fiscal Dialog - Escolha VD ou Factura */}
      <FiscalSaleDialog
        open={fiscalDialogOpen}
        onOpenChange={setFiscalDialogOpen}
        sale={completedSale}
        onComplete={() => {
          setFiscalDialogOpen(false);
          setPrintDialogOpen(true);
          loadSales();
        }}
      />
    </div>
    </ModuleStateBoundary>
  );
}
